import React, { useMemo, useState, useEffect, Fragment, forwardRef, useImperativeHandle, useRef } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Form, 
  Table, 
  Accordion, 
  Badge, 
  Alert,
  InputGroup,
  ButtonGroup,
  Dropdown,
  Tooltip,
  OverlayTrigger,
  ProgressBar,
  Modal,
  ListGroup
} from "react-bootstrap";
import { toastError } from "../../utils/helpers/helper";
import { useDispatch, useSelector } from "react-redux";
import { setCleaningCalculatorValues, loadCleaningCalculatorEstimate } from "../../store/actions/mortgage_calculator/mortgage_calculator.actions";

// Import all helper functions and constants
import {
  round,
  clamp,
  fmt,
  fmt0,
  fmtRate,
  travelTotal,
  nz,
  COUNTRY_OPTIONS,
  STATE_PROVINCE_OPTIONS,
  LOCATION_FACTORS,
  getAutoLaborRate,
  getAutoTMRate,
  getAutoLiftDaily,
  defaultLiftDaysFromVisits,
  PROJECT_TYPES,
  TYPE_PRODUCTIVITY,
  TYPE_RATE_MULT,
  sizeProductivityMultiplier,
  LEVEL_PENALTY_PER_LEVEL,
  PHASE_PASS_PREMIUM,
  NATIONAL_BASE_SQFT,
  JANITORIAL_BASE_M2_HR,
  JANITORIAL_TYPE_MULT,
  PRESSURE_PRESETS,
  estimatePerimeterFromAreaFt2,
  computePressureAreaFromPreset,
  computeAutoVisits,
  computePhasePreset,
  computeBidCalculationWeights,
  computePhaseEffectiveRateMultiplier,
  phaseFactorFromAct,
  deriveSimpleFromMix
} from './cleaningCalculatorHelpers';
import { useFeatureAccess } from "../../hooks/useFeatureAccess";
import { setFreeModeSqFt } from "../../store/actions/users/users.actions";
import { USER_SET_FREE_MODE_SQFT_SUCCESS } from "../../store/constants/userConstants";

const CleaningCalculator = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const calculatorState = useSelector(state => state.cleaningCalculator);
  const { user } = useSelector((state) => state.auth);
  const [units, setUnits] = useState(calculatorState.units || "ft");
  const [currency, setCurrency] = useState(calculatorState.currency || "USD");
  const [estimateName, setEstimateName] = useState(calculatorState.estimateName || "");
  const [estimateDesc, setEstimateDesc] = useState(calculatorState.estimateDesc || "");
  const [country, setCountry] = useState(calculatorState.country || "United States");
  const [stateProvince, setStateProvince] = useState(calculatorState.stateProvince || "California");
  const [building, setBuilding] = useState(calculatorState.building || {
    area: 15000,
    levels: 1,
    useDetailed: false,
    autoArea: true,
    details: [ { name: 'Bldg A', area: 0 }, { name: 'Bldg B', area: 0 }, { name: 'Bldg C', area: 0 } ],
  });
  const [phaseCount, setPhaseCount] = useState(calculatorState.phaseCount || 1);
  const [singlePhase, setSinglePhase] = useState(calculatorState.singlePhase || 'final');
  const [twoPhaseCombo, setTwoPhaseCombo] = useState(calculatorState.twoPhaseCombo || 'rough_final');
  const firstPreset = computePhasePreset(1, 'final', 'rough_final');
  const [activePhases, setActivePhases] = useState(calculatorState.activePhases || firstPreset.act);
  const [weights, setWeights] = useState(calculatorState.weights || firstPreset.w);
  const [phasePasses, setPhasePasses] = useState(calculatorState.phasePasses || { rough: 1, final: 1, fluff: 1 });
  const { hasFeatureAccess, enforceAccess, hasSqftFeatureAccess } = useFeatureAccess("Cleanup Calculator");
  const { free_mode_sqft_count } = useSelector((state) => state.userFreeModeSqFtCount);

  useEffect(() => {
    const isReset = calculatorState.estimateName === "" && calculatorState.estimateDesc === "";
    if (calculatorState.shouldReload || isReset) {
      setUnits(calculatorState.units || "ft");
      setCurrency(calculatorState.currency || "USD");
      setEstimateName(calculatorState.estimateName || "");
      setEstimateDesc(calculatorState.estimateDesc || "");
      setCountry(calculatorState.country || "United States");
      setStateProvince(calculatorState.stateProvince || "California");
      setBuilding(calculatorState.building || {
        area: 15000,
        levels: 1,
        useDetailed: false,
        autoArea: true,
        details: [ { name: 'Bldg A', area: 0 }, { name: 'Bldg B', area: 0 }, { name: 'Bldg C', area: 0 } ],
      });
      setPhaseCount(calculatorState.phaseCount || 1);
      setSinglePhase(calculatorState.singlePhase || 'final');
      setTwoPhaseCombo(calculatorState.twoPhaseCombo || 'rough_final');
      const preset = computePhasePreset(calculatorState.phaseCount || 1, calculatorState.singlePhase || 'final', calculatorState.twoPhaseCombo || 'rough_final');
      setActivePhases(calculatorState.activePhases || preset.act);
      setWeights(calculatorState.weights || preset.w);
      setPhasePasses(calculatorState.phasePasses || { rough: 1, final: 1, fluff: 1 });
      
      setPressure(calculatorState.pressure || { enabled: true, addToBid: false, area: 0, rate_per_unit: 0.2, preset: 'manual' });
      setFlooring(calculatorState.flooring || { enabled: true, addToBid: false, area: 0, coats: 1, price_per_coat: 0.14 });
      setUnitsBaths(calculatorState.unitsBaths || {
        enabled: true,
        addToBid: false,
        units: 0,
        base_rate_per_unit: 0,
        rate_per_unit: 0,
        baths: 0,
        base_rate_per_bath: 0,
        rate_per_bath: 0,
        userEdited: false
      });
      setUnitsMix(calculatorState.unitsMix || {
        enabled: true,
        addToBid: false,
        userEdited: false,
        rows: [
          { type: 'Studio', baseRate: 200, ratePerBath: 50, baseRateUnscaled: 200, ratePerBathUnscaled: 50, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 500 },
          { type: '1 BR', baseRate: 250, ratePerBath: 55, baseRateUnscaled: 250, ratePerBathUnscaled: 55, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 750 },
          { type: '2 BR', baseRate: 300, ratePerBath: 60, baseRateUnscaled: 300, ratePerBathUnscaled: 60, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1000 },
          { type: '3 BR', baseRate: 350, ratePerBath: 65, baseRateUnscaled: 350, ratePerBathUnscaled: 65, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1250 },
          { type: '4 BR', baseRate: 400, ratePerBath: 70, baseRateUnscaled: 400, ratePerBathUnscaled: 70, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1500 },
          { type: '5 BR', baseRate: 450, ratePerBath: 75, baseRateUnscaled: 450, ratePerBathUnscaled: 75, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1750 },
        ]
      });
      setWindows(calculatorState.windows || { enabled: true, addToBid: false, mode: 'auto', panes: 0, price_per_pane: 5, density_per_1000ft2: 4, include_lift: false, lift_cost_daily: 0, lift_days: 0, units_count: 0, panes_per_unit: 12 });
      setTravel(calculatorState.travel || { enabled: true, addToBid: false, auto: true, visits: 0, cost_per_visit: 0 });
      
      setProjectType(calculatorState.projectType || "Corporate");
      // setAutoLabor(calculatorState.autoLabor !== undefined ? calculatorState.autoLabor : true);
      setLabor(calculatorState.labor || { rate_per_hour: getInitialLaborRate(), crew_size: getInitialCrewSize() });
      setSelectedModel(calculatorState.selectedModel || "lean");
      setShowPhaseBreakout(calculatorState.showPhaseBreakout !== undefined ? calculatorState.showPhaseBreakout : true);
      
      setManHour(calculatorState.manHour || { enabled: false, bill_rate_per_hour: 65, apply_to_selected: false, override: false, manual_hours: 0 });
      
      setDavis(calculatorState.davis || { checked: false, percentage: davis || 30 });
      setUnion(calculatorState.union || { checked: false, percentage: union || 70 });
      
      setCollapsed(calculatorState.collapsed || {
        project: false,
        phases: true,
        pricing: false,
        ops: true,
        unitsBaths: true,
        unitsMix: true,
        windows: true,
        pressure: true,
        flooring: true,
        janitorial: true,
        proposalExport: true
      });
      
      setJanitorial(calculatorState.janitorial || {
        enabled: true,
        addToBid: false,
        visits_per_day: 1,
        days_per_week: 5,
        weeks_per_month: 4.33,
        scope: 'standard',
        apply_to_selected: false,
        apply_basis: 'monthly',
        preset: '',
        supplies_pct: 5,
      });
      
      let nationalBaseVal = NATIONAL_BASE_SQFT;
      if (calculatorState.nationalBaseSqft != null) {
        const v = parseFloat(calculatorState.nationalBaseSqft);
        if (!isNaN(v) && v >= 0) nationalBaseVal = v;
      } else {
        try {
          const saved = localStorage.getItem(`last_national_base_${user?.id}`);
          if (saved) { const v = parseFloat(saved); if (!isNaN(v) && v >= 0) nationalBaseVal = v; }
        } catch (e) {}
      }
      setNationalBaseSqft(nationalBaseVal);
      
      dispatch(setCleaningCalculatorValues({ shouldReload: false }));
    }
  }, [calculatorState.shouldReload, calculatorState.estimateName, calculatorState.estimateDesc, dispatch]);

  const [pressure, setPressure] = useState(calculatorState.pressure || { enabled: true, addToBid: false, area: 0, rate_per_unit: 0.2, preset: 'manual' });
  const [flooring, setFlooring] = useState(calculatorState.flooring || { enabled: true, addToBid: false, area: 0, coats: 1, price_per_coat: 0.14 });
  const [unitsBaths, setUnitsBaths] = useState(calculatorState.unitsBaths || {
    enabled: true,
    addToBid: false,
    units: 0,
    base_rate_per_unit: 0,
    rate_per_unit: 0,
    baths: 0,
    base_rate_per_bath: 0,
    rate_per_bath: 0,
    userEdited: false
  });
  const [unitsMix, setUnitsMix] = useState(calculatorState.unitsMix || {
    enabled: true,
    addToBid: false,
    userEdited: false,
    rows: [
      { type: 'Studio', baseRate: 200, ratePerBath: 50, baseRateUnscaled: 200, ratePerBathUnscaled: 50, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 500 },
      { type: '1 BR', baseRate: 250, ratePerBath: 55, baseRateUnscaled: 250, ratePerBathUnscaled: 55, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 750 },
      { type: '2 BR', baseRate: 300, ratePerBath: 60, baseRateUnscaled: 300, ratePerBathUnscaled: 60, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1000 },
      { type: '3 BR', baseRate: 350, ratePerBath: 65, baseRateUnscaled: 350, ratePerBathUnscaled: 65, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1250 },
      { type: '4 BR', baseRate: 400, ratePerBath: 70, baseRateUnscaled: 400, ratePerBathUnscaled: 70, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1500 },
      { type: '5 BR', baseRate: 450, ratePerBath: 75, baseRateUnscaled: 450, ratePerBathUnscaled: 75, units1Bath: 0, units2Bath: 0, units3Bath: 0, sqft: 1750 },
    ]
  });
  const [windows, setWindows] = useState(calculatorState.windows || { enabled: true, addToBid: false, mode: 'auto', panes: 0, price_per_pane: 5, density_per_1000ft2: 4, include_lift: false, lift_cost_daily: 0, lift_days: 0, units_count: 0, panes_per_unit: 12 });
  const [travel, setTravel] = useState(calculatorState.travel || { enabled: true, addToBid: false, auto: true, visits: 0, cost_per_visit: 0 });

  // Productivity + pricing
  const [baseProd] = useState({ rough_m2_hr: 120, final_m2_hr: 90, fluff_m2_hr: 160 });
  const [projectType, setProjectType] = useState(calculatorState.projectType || "Corporate");
  // const [autoLabor, setAutoLabor] = useState(calculatorState.autoLabor !== undefined ? calculatorState.autoLabor : true);
  const getInitialLaborRate = () => {
    const stateKey = calculatorState.stateProvince || "California";
    if (user?.id) {
      const savedRate = localStorage.getItem(`last_labor_rate_${user.id}_${stateKey}`);
      if (savedRate) return parseFloat(savedRate);
    }
    if (calculatorState.labor?.rate_per_hour) {
      return calculatorState.labor.rate_per_hour;
    }
    return 45;
  };
  
  const getInitialCrewSize = () => {
    const stateKey = calculatorState.stateProvince || "California";
    if (user?.id) {
      const savedSize = localStorage.getItem(`last_crew_size_${user.id}_${stateKey}`);
      if (savedSize) return parseInt(savedSize);
    }
    if (calculatorState.labor?.crew_size) {
      return calculatorState.labor.crew_size;
    }
    return 4; // default
  };
  
  const [labor, setLabor] = useState({ 
    rate_per_hour: getInitialLaborRate(), 
    crew_size: getInitialCrewSize() 
  });
  const [margins] = useState({ market_win: 0.07, lean: 0.15, profit_heavy: 0.28 });
  const [selectedModel, setSelectedModel] = useState(calculatorState.selectedModel || "lean");
  const [showPhaseBreakout, setShowPhaseBreakout] = useState(calculatorState.showPhaseBreakout !== undefined ? calculatorState.showPhaseBreakout : true);
  const [simpleSuggest, setSimpleSuggest] = useState({ unit: 0, bath: 0 });
  const [showModelInfo, setShowModelInfo] = useState(false);
  const [showItemizationInfo, setShowItemizationInfo] = useState(false);
  const [manHour, setManHour] = useState(calculatorState.manHour || { enabled: false, bill_rate_per_hour: 65, apply_to_selected: false, override: false, manual_hours: 0 });
  const [davis, setDavis] = useState(calculatorState.davis || { checked: false, percentage: 30 });
  const [union, setUnion] = useState(calculatorState.union || { checked: false, percentage: 70 });
  const [nationalBaseSqft, setNationalBaseSqft] = useState(() => {
    if (calculatorState.nationalBaseSqft != null) {
      const val = parseFloat(calculatorState.nationalBaseSqft);
      if (!isNaN(val) && val >= 0) return val;
    }
    try {
      const saved = localStorage.getItem(`last_national_base_${user?.id}`);
      if (saved != null && saved !== '') {
        const val = parseFloat(saved);
        if (!isNaN(val) && val >= 0) return val;
      }
    } catch (e) {}
    return NATIONAL_BASE_SQFT;
  });
  const [activeKeys, setActiveKeys] = useState(['project']); // Start with project expanded

  const toggleAccordion = (key) => {
    setActiveKeys(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const [collapsed, setCollapsed] = useState(calculatorState.collapsed || {
    project: false,
    phases: true,
    pricing: false,
    ops: true,
    unitsBaths: true,
    unitsMix: true,
    windows: true,
    pressure: true,
    flooring: true,
    janitorial: true,
    proposalExport: true
  });

  // Recurring Janitorial
  const [janitorial, setJanitorial] = useState(calculatorState.janitorial || {
    enabled: true,
    addToBid: false,
    visits_per_day: 1,
    days_per_week: 5,
    weeks_per_month: 4.33,
    scope: 'standard',
    apply_to_selected: false,
    apply_basis: 'monthly',
    preset: '',
    supplies_pct: 5,
  });

  const [scenario, setScenario] = useState("");

  useEffect(() => {
    dispatch(setCleaningCalculatorValues({
      units,
      currency,
      estimateName,
      estimateDesc,
      country,
      stateProvince,
      building,
      phaseCount,
      singlePhase,
      twoPhaseCombo,
      activePhases,
      weights,
      phasePasses,
      pressure,
      flooring,
      unitsBaths,
      unitsMix,
      windows,
      travel,
      projectType,
      // autoLabor,
      labor,
      selectedModel,
      showPhaseBreakout,
      manHour,
      davis,
      union,
      janitorial,
      nationalBaseSqft,
      collapsed
    }));
  }, [
    units, currency, estimateName, estimateDesc, country, stateProvince,
    building, phaseCount, singlePhase, twoPhaseCombo, activePhases, weights, phasePasses,
    pressure, flooring, unitsBaths, unitsMix, windows, travel,
    projectType, labor, selectedModel, showPhaseBreakout,
    manHour, davis, union, janitorial, nationalBaseSqft, collapsed
  ]);

  const toggleCollapse = (section) => {
    setCollapsed(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Auto-expand windows when levels > 2 (3+ stories)
  useEffect(() => {
    if (building.levels > 2) {
      setCollapsed(prev => ({ ...prev, windows: false }));
    }
  }, [building.levels]);

  useEffect(() => {
    if (!building.useDetailed || !building.autoArea) return;
    const total = building.details.reduce((s, d) => s + (Math.max(0, Number(d.area) || 0)), 0);
    if (total !== building.area) setBuilding(prev => ({ ...prev, area: total }));
  }, [building.useDetailed, building.autoArea, building.details, building.area]);

  function applyJanitorialPreset(presetKey){
    const presets = {
      'office_5x': { visits_per_day: 1, days_per_week: 5, scope: 'standard' },
      'retail_7x': { visits_per_day: 1, days_per_week: 7, scope: 'light' },
      'industrial_3x': { visits_per_day: 1, days_per_week: 3, scope: 'standard' },
      'school_5x': { visits_per_day: 1, days_per_week: 5, scope: 'standard' },
      'healthcare_7x_deep': { visits_per_day: 1, days_per_week: 7, scope: 'deep' },
    };
    const p = presets[presetKey];
    if (!p) return;
    setJanitorial(prev => ({ ...prev, ...p, preset: presetKey, weeks_per_month: prev.weeks_per_month || 4.33 }));
  }

  const applyScenario = (key) => {
    switch (key) {
      case 'warehouse100k_final_preset':
        handleUnitsChange('ft'); setCountry('United States'); handleStateChange('Texas'); handleProjectTypeChange('Warehouse');
        setBuilding((b)=>({ ...b, area: 100000, levels: 1, useDetailed: false }));
        setPhaseCount(1); setSinglePhase('final');
        setWindows((w)=>({ ...w, enabled: false }));
        setPressure((p)=>({ ...p, enabled: true, preset: 'around' }));
        setFlooring((f)=>({ ...f, enabled: false }));
        setUnitsMix((u)=>({ ...u, enabled: false }));
        setUnitsBaths((s)=>({ ...s, enabled: false }));
        setJanitorial((j)=>({ ...j, enabled: false, apply_to_selected: false }));
        setSelectedModel('market_win');
        break;
      case 'three_bldgs_mixed_sizes':
        handleUnitsChange('ft'); setCountry('United States'); handleStateChange('Texas'); handleProjectTypeChange('Industrial');
        setBuilding(()=>({ useDetailed: true, autoArea: true, details: [ {name:'Bldg A', area:6000},{name:'Bldg B', area:9000},{name:'Bldg C', area:5000} ], area: 6000+9000+5000, levels: 2 }));
        setPhaseCount(2); setTwoPhaseCombo('rough_final');
        setWindows((w)=>({ ...w, enabled: true, mode: 'auto', price_per_pane: 5 }));
        setPressure((p)=>({ ...p, enabled: false, preset: 'manual', area: 0 }));
        setFlooring((f)=>({ ...f, enabled: false }));
        setUnitsMix((u)=>({ ...u, enabled: false }));
        setUnitsBaths((s)=>({ ...s, enabled: false }));
        setJanitorial((j)=>({ ...j, enabled: false, apply_to_selected: false }));
        setSelectedModel('lean');
        break;
      case 'corp_30k_10lvls_windows':
        handleUnitsChange('ft'); setCountry('United States'); handleStateChange('New York'); handleProjectTypeChange('Corporate');
        setBuilding((b)=>({ ...b, area: 30000, levels: 10, useDetailed: false }));
        setPhaseCount(2); setTwoPhaseCombo('final_fluff');
        setWindows((w)=>({ ...w, enabled: true, mode: 'auto', density_per_1000ft2: 6, price_per_pane: 6 }));
        setPressure((p)=>({ ...p, enabled: false, preset: 'manual', area: 0 }));
        setFlooring((f)=>({ ...f, enabled: false }));
        setUnitsMix((u)=>({ ...u, enabled: false }));
        setUnitsBaths((s)=>({ ...s, enabled: false }));
        setJanitorial((j)=>({ ...j, enabled: false, apply_to_selected: false }));
        setSelectedModel('profit_heavy');
        break;
      case 'apt_unitsmix_120':
        handleUnitsChange('ft'); setCountry('United States'); handleStateChange('Florida'); handleProjectTypeChange('Apartment Complex');
        setBuilding((b)=>({ ...b, area: 120000, levels: 4, useDetailed: false }));
        setPhaseCount(1); setSinglePhase('final');
        setUnitsBaths((s)=>({ ...s, enabled: false }));
        setUnitsMix((u)=>({ ...u, enabled: true, userEdited: true, rows: u.rows.map((r)=>{
          if (r.type === '1 BR') return { ...r, units1Bath: 60 };
          if (r.type === '2 BR') return { ...r, units2Bath: 40 };
          if (r.type === '3 BR') return { ...r, units2Bath: 20 };
          return { ...r, units1Bath: 0, units2Bath: 0, units3Bath: 0 };
        }) }));
        setWindows((w)=>({ ...w, enabled: false }));
        setPressure((p)=>({ ...p, enabled: false, preset: 'manual', area: 0 }));
        setFlooring((f)=>({ ...f, enabled: false }));
        setJanitorial((j)=>({ ...j, enabled: false, apply_to_selected: false }));
        setSelectedModel('lean');
        break;
      case 'janitorial_office_25k_5x':
        handleUnitsChange('ft'); setCountry('United States'); handleStateChange('Colorado'); handleProjectTypeChange('Corporate');
        setBuilding((b)=>({ ...b, area: 25000, levels: 2, useDetailed: false }));
        setPhaseCount(1); setSinglePhase('final');
        setWindows((w)=>({ ...w, enabled: false }));
        setPressure((p)=>({ ...p, enabled: false, preset: 'manual', area: 0 }));
        setFlooring((f)=>({ ...f, enabled: false }));
        setUnitsMix((u)=>({ ...u, enabled: false }));
        setUnitsBaths((s)=>({ ...s, enabled: false }));
        setJanitorial((j)=>({ ...j, enabled: true, preset: 'office_5x', visits_per_day: 1, days_per_week: 5, weeks_per_month: 4.33, scope: 'standard', apply_basis: 'monthly', apply_to_selected: true }));
        setSelectedModel('lean');
        break;
      case 'house_oh_5br_3ba_2story_windows':
        handleUnitsChange('ft'); setCountry('United States'); handleStateChange('Ohio'); handleProjectTypeChange('Residential Home');
        setBuilding((b)=>({ ...b, area: 3000, levels: 2, useDetailed: false }));
        setPhaseCount(1); setSinglePhase('final');
        setUnitsMix((u)=>({ ...u, enabled: false }));
        setUnitsBaths((s)=>({ ...s, enabled: true, units: 5, baths: 3, userEdited: false }));
        setWindows((w)=>({ ...w, enabled: true, mode: 'auto', price_per_pane: 5 }));
        setPressure((p)=>({ ...p, enabled: false, preset: 'manual', area: 0 }));
        setFlooring((f)=>({ ...f, enabled: false }));
        setJanitorial((j)=>({ ...j, enabled: false, apply_to_selected: false }));
        setSelectedModel('lean');
        break;
      default: break;
    }
  };

  // Sync phases preset→act/weights
  useEffect(() => {
    const preset = computePhasePreset(phaseCount, singlePhase, twoPhaseCombo);
    setActivePhases(preset.act); setWeights(preset.w);
    setPhasePasses({ rough: 1, final: 1, fluff: 1 });
  }, [phaseCount, singlePhase, twoPhaseCombo]);

  useEffect(() => {
    const specialProjectTypes = [
      "Senior living Retirement",
      "Apartment Complex",
      "Hotel",
      "Mixed use",
      "Residential Home"
    ];

    if (specialProjectTypes.includes(projectType)) {
      // setBuilding((prev) => ({ ...prev, area: 0 }));
      setActiveKeys((prev) => {
        const newKeys = new Set(prev);
        newKeys.add('phases');
        newKeys.add('pricing');
        newKeys.add('unitsBaths');
        return Array.from(newKeys);
      });
      setPhaseCount(3);
      setShowPhaseBreakout(false);
    } else {
      setActiveKeys(['project', 'pricing']);   
    }
  }, [projectType]);

  // Auto labor rate
  useEffect(() => {
    // if (!autoLabor) return;
    const nextLabor = getAutoLaborRate(country, stateProvince);
    const nextTM = getAutoTMRate(country, stateProvince);

    const stateKey = stateProvince || "California";
    const savedRate = localStorage.getItem(`last_labor_rate_${user?.id}_${stateKey}`);

    setLabor((l) => ({ ...l, rate_per_hour: savedRate ? parseFloat(savedRate) : nextLabor }));
    setManHour((m) => ({ ...m, bill_rate_per_hour: nextTM }));
  }, [country, stateProvince]);

  // Units Mix defaults seeding (counts only) – NO size profile multiplier
  useEffect(() => {
    if (unitsMix.userEdited) return;
    const baselineLabor = country === 'United States' ? 40 : 38;
    const laborRatio = (labor.rate_per_hour || baselineLabor) / baselineLabor;
    const loc = LOCATION_FACTORS[stateProvince] || 1;
    const seedMult = (0.85 * laborRatio + 0.15 * loc);
    const baseDefaults = [ { base: 200, bath: 50 },{ base: 250, bath: 55 },{ base: 300, bath: 60 },{ base: 350, bath: 65 },{ base: 400, bath: 70 },{ base: 450, bath: 75 } ];
    const pf = phaseFactorFromAct(activePhases);
    setUnitsMix(prev => ({ ...prev, rows: prev.rows.map((r, i) => {
      const baseSeed = (baseDefaults[i]?.base) ?? (r.baseRateUnscaled ?? r.baseRate ?? 0);
      const bathSeed = (baseDefaults[i]?.bath) ?? (r.ratePerBathUnscaled ?? r.ratePerBath ?? 0);
      const baseUn = Math.round(baseSeed * seedMult);
      const bathUn = Math.round(bathSeed * seedMult);
      return { ...r, baseRateUnscaled: baseUn, ratePerBathUnscaled: bathUn, baseRate: round(baseUn * pf), ratePerBath: round(bathUn * pf) };
    }) }));
  }, [country, stateProvince, labor.rate_per_hour, activePhases, unitsMix.userEdited]);

  // Simple Rooms/Units defaults from Units Mix
  useEffect(() => {
    const { perUnit, perBath } = deriveSimpleFromMix(unitsMix.rows, true);
    setSimpleSuggest({ unit: perUnit, bath: perBath });
    const pf = phaseFactorFromAct(activePhases);
    setUnitsBaths(prev => prev.userEdited ? { ...prev } : { ...prev, base_rate_per_unit: perUnit, base_rate_per_bath: perBath, rate_per_unit: round(perUnit * pf), rate_per_bath: round(perBath * pf) });
  }, [activePhases, unitsMix.rows]);

  // Rescale SIMPLE when phases change
  useEffect(() => {
    const pf = phaseFactorFromAct(activePhases);
    setUnitsBaths(prev => ({ ...prev, rate_per_unit: round((prev.base_rate_per_unit || 0) * pf), rate_per_bath: round((prev.base_rate_per_bath || 0) * pf) }));
  }, [activePhases]);

  // Rescale Units Mix rates when phases change (no size profile)
  useEffect(() => {
    const pf = phaseFactorFromAct(activePhases);
    setUnitsMix(prev => ({ ...prev, rows: prev.rows.map(r => {
      const baseUn = (r.baseRateUnscaled ?? r.baseRate ?? 0);
      const bathUn = (r.ratePerBathUnscaled ?? r.ratePerBath ?? 0);
      return { ...r, baseRateUnscaled: baseUn, ratePerBathUnscaled: bathUn, baseRate: round(baseUn * pf), ratePerBath: round(bathUn * pf) };
    }) }));
  }, [activePhases]);

  // Effective productivity
  const effProd = useMemo(() => {
    const totalArea = building.area;
    const areaM2 = units === "ft" ? totalArea * 0.092903 : totalArea;
    const sizeMult = sizeProductivityMultiplier(areaM2);
    const lvlMult = 1 + (Math.max(1, building.levels) - 1) * LEVEL_PENALTY_PER_LEVEL;
    const typeMult = TYPE_PRODUCTIVITY[projectType] || TYPE_PRODUCTIVITY.default;
    const locMult = LOCATION_FACTORS[stateProvince] || LOCATION_FACTORS["__default"];
    return {
      rough_m2_hr: (baseProd.rough_m2_hr * (typeMult.rough || 1) * sizeMult) / (lvlMult * locMult),
      final_m2_hr: (baseProd.final_m2_hr * (typeMult.final || 1) * sizeMult) / (lvlMult * locMult),
      fluff_m2_hr: (baseProd.fluff_m2_hr * (typeMult.fluff || 1) * sizeMult) / (lvlMult * locMult)
    };
  }, [units, building.area, building.levels, baseProd, projectType, stateProvince]);

  // Auto-apply pressure preset when active
  useEffect(() => {
    if (!pressure.enabled) return;
    if (pressure.preset === 'manual') return;
    const areaFt2 = units === 'ft' ? building.area : building.area / 0.092903;
    const est = computePressureAreaFromPreset(areaFt2, pressure.preset);
    setPressure(prev => ({ ...prev, area: est }));
  }, [pressure.enabled, pressure.preset, building.area, units]);

  // Core calculation
  const calc = useMemo(() => {
    const totalArea = building.area; // in chosen units
    const areaM2 = units === "ft" ? totalArea * 0.092903 : totalArea;
    const areaFt2 = units === 'ft' ? totalArea : totalArea / 0.092903;

    // Use separate bid calculation weights (not normalized, allows >100% total)
    const bidWeights = computeBidCalculationWeights(phaseCount, singlePhase, twoPhaseCombo);
    const activeBidWeights = {
      rough: activePhases.rough ? bidWeights.rough : 0,
      final: activePhases.final ? bidWeights.final : 0,
      fluff: activePhases.fluff ? bidWeights.fluff : 0
    };

    // Convert percentages to decimals and use directly (don't normalize)
    const wR = activeBidWeights.rough / 100;
    const wF = activeBidWeights.final / 100;
    const wFl = activeBidWeights.fluff / 100;

    const roughHrsBase = activePhases.rough ? (areaM2 * wR) / Math.max(1, effProd.rough_m2_hr) : 0;
    const finalHrsBase = activePhases.final ? (areaM2 * wF) / Math.max(1, effProd.final_m2_hr) : 0;
    const fluffHrsBase = activePhases.fluff ? (areaM2 * wFl) / Math.max(1, effProd.fluff_m2_hr) : 0;

    const roughHrs = roughHrsBase * (activePhases.rough ? Math.max(1, phasePasses.rough) : 0);
    const finalHrs = finalHrsBase * (activePhases.final ? Math.max(1, phasePasses.final) : 0);
    const fluffHrs = fluffHrsBase * (activePhases.fluff ? Math.max(1, phasePasses.fluff) : 0);

    const laborHours = roughHrs + finalHrs + fluffHrs;
    const laborCost = laborHours * labor.rate_per_hour;

    // Pressure Wash - always calculated, only included in bid if addToBid is checked
    const pressureCost = Math.max(0, pressure.area) * Math.max(0, pressure.rate_per_unit);
    const pressureCostForBid = pressure.addToBid ? pressureCost : 0;

    // Special flooring - always calculated, only included in bid if addToBid is checked
    const flooringCost = Math.max(0, flooring.area) * Math.max(1, flooring.coats) * Math.max(0, flooring.price_per_coat);
    const flooringCostForBid = flooring.addToBid ? flooringCost : 0;

    // Simple Units/Baths - always calculated, only included in bid if addToBid is checked
    const unitsBathsTotalRaw = (Math.max(0, unitsBaths.units) * Math.max(0, unitsBaths.rate_per_unit) + Math.max(0, unitsBaths.baths) * Math.max(0, unitsBaths.rate_per_bath));
    const unitsBathsCost = (unitsBaths.addToBid && !unitsMix.addToBid) ? unitsBathsTotalRaw : 0;

    // Units Mix (counts only) - always calculated, only included in bid if addToBid is checked
    const unitsMixTotalRaw = unitsMix.rows.reduce((sum, r) => {
      const u1 = Math.max(0, parseInt(r.units1Bath ?? 0, 10));
      const u2 = Math.max(0, parseInt(r.units2Bath ?? 0, 10));
      const u3 = Math.max(0, parseInt(r.units3Bath ?? 0, 10));
      const base = (u1 + u2 + u3) * Math.max(0, parseFloat(r.baseRate ?? 0));
      const baths = (u1 * 1 + u2 * 2 + u3 * 3) * Math.max(0, parseFloat(r.ratePerBath ?? 0));
      return sum + base + baths;
    }, 0);
    const unitsMixCost = unitsMix.addToBid ? unitsMixTotalRaw : 0;

    // Calculate totals per row for Units Mix
    const unitsMixRowTotals = unitsMix.rows.map(r => {
      const u1 = Math.max(0, parseInt(r.units1Bath ?? 0, 10));
      const u2 = Math.max(0, parseInt(r.units2Bath ?? 0, 10));
      const u3 = Math.max(0, parseInt(r.units3Bath ?? 0, 10));
      const totalUnits = u1 + u2 + u3;
      const totalBaths = (u1 * 1) + (u2 * 2) + (u3 * 3);
      const baseTotal = totalUnits * Math.max(0, parseFloat(r.baseRate ?? 0));
      const bathTotal = totalBaths * Math.max(0, parseFloat(r.ratePerBath ?? 0));
      const rowTotal = baseTotal + bathTotal;
      return {
        totalUnits,
        totalBaths,
        baseTotal,
        bathTotal,
        rowTotal
      };
    });

    // Windows (auto density × levels, manual panes, or by-units) - always calculated
    let windowCost = 0; let windowPanesComputed = 0; let liftCost = 0;
    const areaFt2Win = areaFt2;
    const levelFactor = 1 + (Math.max(1, building.levels) - 1) * LEVEL_PENALTY_PER_LEVEL;
    let baseWindow = 0;
    if (windows.mode === 'panes') {
      const panes = Math.max(0, windows.panes || 0);
      windowPanesComputed = panes;
      baseWindow = panes * Math.max(0, windows.price_per_pane) * levelFactor;
    } else if (windows.mode === 'units') {
      const windowUnits = Math.max(0, windows.units_count || 0);
      const ppu = Math.max(0, windows.panes_per_unit || 0);
      const panes = windowUnits * ppu;
      windowPanesComputed = panes;
      baseWindow = panes * Math.max(0, windows.price_per_pane) * levelFactor;
    } else {
      const density = Math.max(0, windows.density_per_1000ft2);
      let panes = 0;
      
      if (areaFt2Win > 0) {
        // Standard calculation using building area
        panes = density * (areaFt2Win / 1000) * Math.max(1, building.levels);
      } else if (unitsBaths.units > 0) {
        // Fallback: use Simple Rooms units when building area is 0
        const panesPerUnit = density; // Use density as panes per unit
        panes = unitsBaths.units * panesPerUnit * Math.max(1, building.levels);
      }
      
      windowPanesComputed = panes;
      baseWindow = panes * Math.max(0, windows.price_per_pane) * levelFactor;
    }
    const needLift = building.levels > 2; // 3+ stories rule
    if (needLift || windows.include_lift) {
      const liftDaily = windows.lift_cost_daily || Math.round(getAutoLiftDaily(stateProvince));
      const liftDays = windows.lift_days || defaultLiftDaysFromVisits(travel.visits);
      liftCost = Math.max(0, liftDaily) * Math.max(0, liftDays);
    }
    windowCost = baseWindow + liftCost;
    const windowCostForBid = windows.addToBid ? windowCost : 0;

    // Travel - always calculated
    const travelCost = travelTotal(travel.visits, travel.cost_per_visit);
    const travelCostForBid = travel.addToBid ? travelCost : 0;

    // Phase factor no longer needed since bid weights handle multi-phase premium
    const enabledPhasesCount = Number(activePhases.rough) + Number(activePhases.final) + Number(activePhases.fluff);
    const phaseFactor = 1.0; // Bid calculation weights now handle multi-phase complexity

    const locRateMult = LOCATION_FACTORS[stateProvince] || 1;
    const typeRateMult = TYPE_RATE_MULT[projectType] || TYPE_RATE_MULT.default;
    const phaseRateMult = computePhaseEffectiveRateMultiplier(phaseCount, singlePhase, twoPhaseCombo);
    const effectiveSqftRate = Math.max(0, nationalBaseSqft) * locRateMult * typeRateMult * phaseRateMult;
    const effectiveSqftCharge = Math.max(0, areaFt2) * effectiveSqftRate;

    const basePhaseCost = (laborCost * phaseFactor);
    const extrasPreMargin = pressureCostForBid + flooringCostForBid + unitsBathsCost + unitsMixCost + windowCostForBid + travelCostForBid;
    const basePreMargin = basePhaseCost + extrasPreMargin + effectiveSqftCharge; // no minimum-by-area floor

    // Calculate base totals
    const baseTotals = {
      market_win: Math.min(basePreMargin * (1 + margins.market_win), 999999999999999), // Cap at reasonable maximum
      lean: Math.min(basePreMargin * (1 + margins.lean), 999999999999999),
      profit_heavy: Math.min(basePreMargin * (1 + margins.profit_heavy), 999999999999999)
    };

    // Apply Davis-Bacon and Union wage percentage increases
    let wagePercent = 0;
    if (davis.checked) wagePercent += Math.max(0, parseFloat(davis.percentage) || 0);
    if (union.checked) wagePercent += Math.max(0, parseFloat(union.percentage) || 0);
    const wageMultiplier = 1 + (wagePercent / 100);

    const totals = {
      market_win: Math.min(baseTotals.market_win * wageMultiplier, 999999999999999),
      lean: Math.min(baseTotals.lean * wageMultiplier, 999999999999999),
      profit_heavy: Math.min(baseTotals.profit_heavy * wageMultiplier, 999999999999999)
    };

    // Man-Hour (T&M)
    const tmHours = (manHour.enabled && manHour.override && isFinite(manHour.manual_hours)) ? Math.max(0, manHour.manual_hours) : laborHours;
    const baseManHourTotal = Math.min((tmHours * (manHour.bill_rate_per_hour || 0)) + pressureCostForBid + flooringCostForBid + unitsBathsCost + unitsMixCost + windowCostForBid + travelCostForBid, 999999999999999);
    const manHourTotal = Math.min(baseManHourTotal * wageMultiplier, 999999999999999);

    // Janitorial
    const sizeMult = sizeProductivityMultiplier(areaM2);
    const lvlMult = 1 + (Math.max(1, building.levels) - 1) * LEVEL_PENALTY_PER_LEVEL;
    const locMult = LOCATION_FACTORS[stateProvince] || 1;
    const typeMultJan = JANITORIAL_TYPE_MULT[projectType] || JANITORIAL_TYPE_MULT.default;
    const scopeMult = janitorial.scope === 'light' ? 1.15 : janitorial.scope === 'deep' ? 0.80 : 1.0;
    const janEff = (JANITORIAL_BASE_M2_HR * typeMultJan * sizeMult * scopeMult) / (lvlMult * locMult);

    const janHoursPerVisit = areaM2 / Math.max(1, janEff);
    const janLaborPerVisit = janHoursPerVisit * labor.rate_per_hour;
    const janBasePerVisit = janLaborPerVisit;

    // Base per-visit pricing with margin
    const baseJanTotalsPerVisit = {
      market_win: Math.min(janBasePerVisit * (1 + margins.market_win), 999999999999999),
      lean: Math.min(janBasePerVisit * (1 + margins.lean), 999999999999999),
      profit_heavy: Math.min(janBasePerVisit * (1 + margins.profit_heavy), 999999999999999)
    };
    // Apply wage multiplier to per-visit pricing
    const janTotalsPerVisit = {
      market_win: Math.min(baseJanTotalsPerVisit.market_win * wageMultiplier, 999999999999999),
      lean: Math.min(baseJanTotalsPerVisit.lean * wageMultiplier, 999999999999999),
      profit_heavy: Math.min(baseJanTotalsPerVisit.profit_heavy * wageMultiplier, 999999999999999)
    };

    const visitsWeek = Math.max(0, janitorial.visits_per_day) * Math.max(0, janitorial.days_per_week);
    const visitsMonth = visitsWeek * Math.max(0, janitorial.weeks_per_month);

    // Weekly is per-visit × visits per week (already has wage multiplier from janTotalsPerVisit)
    const janTotalsWeekly = {
      market_win: Math.min(janTotalsPerVisit.market_win * visitsWeek, 999999999999999),
      lean: Math.min(janTotalsPerVisit.lean * visitsWeek, 999999999999999),
      profit_heavy: Math.min(janTotalsPerVisit.profit_heavy * visitsWeek, 999999999999999),
    };
    const janBasePerMonth = janBasePerVisit * visitsMonth;
    const suppliesPct = Math.max(0, janitorial.supplies_pct || 0) / 100;
    const janSuppliesMonthly = janBasePerMonth * suppliesPct;
    const janMonthlyPreMargin = janBasePerMonth + janSuppliesMonthly;
    const baseJanTotalsMonthly = {
      market_win: Math.min(janMonthlyPreMargin * (1 + margins.market_win), 999999999999999),
      lean: Math.min(janMonthlyPreMargin * (1 + margins.lean), 999999999999999),
      profit_heavy: Math.min(janMonthlyPreMargin * (1 + margins.profit_heavy), 999999999999999),
    };
    const janTotalsMonthly = {
      market_win: Math.min(baseJanTotalsMonthly.market_win * wageMultiplier, 999999999999999),
      lean: Math.min(baseJanTotalsMonthly.lean * wageMultiplier, 999999999999999),
      profit_heavy: Math.min(baseJanTotalsMonthly.profit_heavy * wageMultiplier, 999999999999999),
    };
    // Annual is per-visit × visits per week × 52 weeks (janTotalsPerVisit already has wage multiplier)
    const janTotalsAnnual = {
      market_win: Math.min(janTotalsPerVisit.market_win * visitsWeek * 52, 999999999999999),
      lean: Math.min(janTotalsPerVisit.lean * visitsWeek * 52, 999999999999999),
      profit_heavy: Math.min(janTotalsPerVisit.profit_heavy * visitsWeek * 52, 999999999999999),
    };

    // Selected Bid logic
    let selectedBid = totals[selectedModel];
    let selectedBreakdownMode = 'model';
    if (janitorial.enabled && janitorial.apply_to_selected) {
      selectedBreakdownMode = 'janitorial';
      selectedBid = janitorial.apply_basis === 'per_visit' ? janTotalsPerVisit[selectedModel] : janTotalsMonthly[selectedModel];
    }
    if (manHour.apply_to_selected) {
      selectedBreakdownMode = 'tm';
      selectedBid = manHourTotal;
    }

    // Itemization (model path) – post-margin components WITH wage multiplier
    const marginFactor = 1 + (selectedModel === 'market_win' ? margins.market_win : selectedModel === 'lean' ? margins.lean : margins.profit_heavy);
    const combinedFactor = marginFactor * wageMultiplier; // Apply both margin AND wage increases
    const itemizedModelPost = {
      core_phases: basePhaseCost * combinedFactor,
      effective_ft2_seed: effectiveSqftCharge * combinedFactor,
      travel: travelCostForBid * combinedFactor,
      windows: windowCostForBid * combinedFactor,
      pressure: pressureCostForBid * combinedFactor,
      flooring: flooringCostForBid * combinedFactor,
      units_simple: unitsBathsCost * combinedFactor,
      units_mix: unitsMixCost * combinedFactor,
    };
    const itemizedModelPostSum = Object.values(itemizedModelPost).reduce((a,b)=>a+b,0);

    // TM itemization WITH wage multiplier
    const itemizedTm = {
      labor_tm: (tmHours * (manHour.bill_rate_per_hour || 0)) * wageMultiplier,
      travel: travelCostForBid * wageMultiplier,
      windows: windowCostForBid * wageMultiplier,
      pressure: pressureCostForBid * wageMultiplier,
      flooring: flooringCostForBid * wageMultiplier,
      units_simple: unitsBathsCost * wageMultiplier,
      units_mix: unitsMixCost * wageMultiplier,
    };
    const itemizedTmSum = Object.values(itemizedTm).reduce((a,b)=>a+b,0);

    const days = Math.min(laborHours / (Math.max(1, labor.crew_size) * 8), 999999); // Cap at reasonable maximum

    return {
      areaM2, areaFt2,
      roughHrs, finalHrs, fluffHrs,
      laborHours, laborCost,
      pressureCost, flooringCost, unitsBathsCost, unitsBathsTotalRaw, unitsMixCost, unitsMixTotalRaw,
      basePhaseCost, extrasPreMargin, basePreMargin,
      windowCost, windowPanesComputed, liftCost,
      travelCost,
      totals, selectedBid, days,
      phaseFactor, effectiveSqftRate, effectiveSqftCharge, locRateMult, typeRateMult, phaseRateMult,
      windowLevelFactor: 1 + (Math.max(1, building.levels) - 1) * LEVEL_PENALTY_PER_LEVEL,
      marginFactor,
      wageMultiplier, // Export wage multiplier for display
      wagePercent, // Export total wage percentage
      // Man-hour path
      manHourTotal, tmHours, itemizedTm, itemizedTmSum,
      // Model path itemization
      itemizedModelPost, itemizedModelPostSum,
      // Janitorial
      janHoursPerVisit, janLaborPerVisit,
      janTotalsPerVisit, janTotalsWeekly, janTotalsMonthly, janTotalsAnnual, janSuppliesMonthly, visitsWeek, visitsMonth,
      janitorialMonthly: janTotalsMonthly[selectedModel],
      selectedBreakdownMode,
      // Units Mix row totals
      unitsMixRowTotals,
    };
  }, [units, building, weights, activePhases, phasePasses, effProd, labor, margins, selectedModel, pressure, flooring, unitsBaths, unitsMix, windows, projectType, stateProvince, nationalBaseSqft, manHour, davis, union, janitorial, travel, phaseCount, singlePhase, twoPhaseCombo]);

  // Auto-default site visits & cost when auto=true
  useEffect(() => {
    if (!travel.auto) return;
    const autoVisits = computeAutoVisits(calc.laborHours, labor.crew_size, activePhases, phasePasses);
    const defaultCost = travel.cost_per_visit && travel.cost_per_visit > 0 ? travel.cost_per_visit : Math.round(labor.rate_per_hour * 0.5);
    setTravel(prev => {
      const nextVisits = autoVisits;
      const nextCost = prev.cost_per_visit && prev.cost_per_visit > 0 ? prev.cost_per_visit : defaultCost;
      const nextEnabled = autoVisits > 2; // enable only when visits > 2
      if (nextVisits === prev.visits && nextCost === prev.cost_per_visit && nextEnabled === prev.enabled) return prev;
      return { ...prev, visits: nextVisits, cost_per_visit: nextCost, enabled: nextEnabled };
    });
  }, [travel.auto, calc.laborHours, labor.rate_per_hour, labor.crew_size, activePhases, phasePasses]);

  // Sync calculated values to Redux (must come after calc is computed)
  useEffect(() => {
    if (estimateName) {
      dispatch(setCleaningCalculatorValues({
        calculatedBidAmount: calc.selectedBid,
        calculatedDays: calc.days,
        calculatedLaborHours: calc.laborHours,
        calculatedJanitorialMonthly: calc.janitorialMonthly,
        calculatedWindowPanes: calc.windowPanesComputed,
        calculatedWindowPricePerPane: calc.windowPanesComputed > 0 
          ? (calc.itemizedModelPost.windows / calc.windowPanesComputed) 
          : 0,
        calculatedPressurePrice: pressure.area > 0 
          ? (calc.itemizedModelPost.pressure / pressure.area)
          : 0,
      }));
    }
  }, [calc.selectedBid, calc.days, calc.laborHours, calc.janitorialMonthly, calc.windowPanesComputed, calc.itemizedModelPost.windows, calc.itemizedModelPost.pressure, pressure.area, estimateName, dispatch]);

  // Auto-default lift include/cost/days when Windows enabled and 3+ stories
  useEffect(() => {
    if (!windows.enabled) return;
    if (building.levels > 2) {
      setWindows(prev => ({
        ...prev,
        include_lift: true,
        lift_cost_daily: prev.lift_cost_daily || Math.round(getAutoLiftDaily(stateProvince)),
        lift_days: prev.lift_days || defaultLiftDaysFromVisits(travel.visits),
      }));
    }
  }, [windows.enabled, building.levels, stateProvince, travel.visits]);

  const handleDavisPercentageChange = (value) => {
    setDavis(prev => ({ ...prev, percentage: value }));
    localStorage.setItem(`davis_percentage_${user?.id}`, value);
  }

  const handleUnionPercentageChange = (value) => {
    setUnion(prev => ({ ...prev, percentage: value }));
    localStorage.setItem(`union_percentage_${user?.id}`, value);
  }

  const handleCountryChange = (value) => {
    setCountry(value);
    localStorage.setItem(`last_country_${user?.id}`, value);
    handleStateChange(STATE_PROVINCE_OPTIONS[value][0]);
  }

  const handleStateChange = (value) => {
    setStateProvince(value);
    localStorage.setItem(`last_state_${user?.id}`, value);
  }

  const handleCurrencyChange = (value) => {
    setCurrency(value);
    localStorage.setItem(`last_currency_${user?.id}`, value);
  }

  const handleUnitsChange = (value) => {
    setUnits(value);
    localStorage.setItem(`last_units_${user?.id}`, value);
  }

  const handleProjectTypeChange = (value) => {
    setProjectType(value);
    localStorage.setItem(`last_project_type_${user?.id}`, value);
  }

  const handleLaborRateChange = (value) => {
    setLabor(prev => ({ ...prev, rate_per_hour: value }));
    localStorage.setItem(`last_labor_rate_${user?.id}_${stateProvince || "California"}`, value);
  }

  const handleCrewSizeChange = (value) => {
    setLabor(prev => ({ ...prev, crew_size: value }));
    localStorage.setItem(`last_crew_size_${user?.id}_${stateProvince || "California"}`, value);
  }

  const handleNationalBaseChange = (value) => {
    setNationalBaseSqft(value);
    localStorage.setItem(`last_national_base_${user?.id}`, value);
  }

  const handleBillRateChange = (value) => {
    setManHour(prev => ({ ...prev, bill_rate_per_hour: value }));
    localStorage.setItem(`last_bill_rate_${user?.id}`, value);
  }

  const handleSelectModelChange = (value) => {
    setSelectedModel(value);
    localStorage.setItem(`last_selected_model_${user?.id}`, value);
  }

  const handleBuildingAreaChange = (value) => {
    if (hasSqftFeatureAccess) {
      setBuilding({ ...building, area: value });
      dispatch(setFreeModeSqFt());
      dispatch({
        type: USER_SET_FREE_MODE_SQFT_SUCCESS,
        payload: free_mode_sqft_count + 1,
      });
    } else {
      enforceAccess();
    }
  }

  const unitAreaLabel = units === "ft" ? "ft²" : "m²";

  // ----------------- Serialization Functions (for parent component to use) -----------------
  
  // Serialize all calculator state to save to backend
  const serializeCalculatorState = () => {
    return {
      units,
      currency,
      estimateName,
      estimateDesc,
      country,
      stateProvince,
      building,
      phaseCount,
      singlePhase,
      twoPhaseCombo,
      activePhases,
      weights,
      phasePasses,
      pressure,
      flooring,
      unitsBaths,
      unitsMix,
      windows,
      travel,
      projectType,
      // autoLabor,
      labor,
      selectedModel,
      showPhaseBreakout,
      manHour,
      janitorial,
      nationalBaseSqft,
      collapsed
    };
  };

  // Deserialize saved state and restore calculator
  const deserializeCalculatorState = (data) => {
    try {
      if (data.units) setUnits(data.units);
      if (data.currency) setCurrency(data.currency);
      if (data.estimateName) setEstimateName(data.estimateName);
      if (data.estimateDesc) setEstimateDesc(data.estimateDesc);
      if (data.country) setCountry(data.country);
      if (data.stateProvince) setStateProvince(data.stateProvince);
      if (data.building) setBuilding(data.building);
      if (data.phaseCount) setPhaseCount(data.phaseCount);
      if (data.singlePhase) setSinglePhase(data.singlePhase);
      if (data.twoPhaseCombo) setTwoPhaseCombo(data.twoPhaseCombo);
      if (data.activePhases) setActivePhases(data.activePhases);
      if (data.weights) setWeights(data.weights);
      if (data.phasePasses) setPhasePasses(data.phasePasses);
      if (data.pressure) setPressure(data.pressure);
      if (data.flooring) setFlooring(data.flooring);
      if (data.unitsBaths) setUnitsBaths(data.unitsBaths);
      if (data.unitsMix) setUnitsMix(data.unitsMix);
      if (data.windows) setWindows(data.windows);
      if (data.travel) setTravel(data.travel);
      if (data.projectType) setProjectType(data.projectType);
      // if (data.autoLabor !== undefined) setAutoLabor(data.autoLabor);
      if (data.labor) setLabor(data.labor);
      if (data.selectedModel) setSelectedModel(data.selectedModel);
      if (data.showPhaseBreakout !== undefined) setShowPhaseBreakout(data.showPhaseBreakout);
      if (data.manHour) setManHour(data.manHour);
      if (data.janitorial) setJanitorial(data.janitorial);
      if (data.nationalBaseSqft) setNationalBaseSqft(data.nationalBaseSqft);
      if (data.collapsed) setCollapsed(data.collapsed);
    } catch (error) {
      console.error('Error deserializing calculator state:', error);
      toastError('Error loading calculator state');
    }
  };

  // Utility: Get payload for backend save (to be called by parent component)
  const getEstimatePayload = () => {
    // Validate and round calculated values to prevent API errors
    const validateAndRoundValue = (value, fieldName, decimalPlaces = 2, maxDigits = 15) => {
      const numValue = Number(value);
      if (!isFinite(numValue)) {
        throw new Error(`${fieldName} is not a valid number`);
      }
      
      const roundedValue = Math.round(numValue * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
      
      return roundedValue;
    };

    try {
      // Validate and round critical calculated values to 2 decimal places
      const validatedBidAmount = validateAndRoundValue(calc.selectedBid, 'Bid Amount', 2);
      const validatedDays = validateAndRoundValue(calc.days, 'Days Expected', 2);
      const validatedJanitorialMonthly = validateAndRoundValue(calc.janitorialMonthly, 'Janitorial Monthly Rate', 2);

      const calculatorState = serializeCalculatorState();
      return {
        // Basic info
        project_name: estimateName,
        notes: estimateDesc,
        
        // Location
        country: country,
        state: stateProvince,
        
        // Building
        square_foot: building.area,
        building_levels: building.levels,
        
        // Phases
        phase_count: phaseCount,
        single_phase: singlePhase,
        two_phase_combo: twoPhaseCombo,
        phase: phaseCount === 1 ? singlePhase : twoPhaseCombo,
        
        // Project details
        project_type: projectType,
        selected_model: selectedModel,
        
        // Units and currency
        units_type: units,
        currency_type: currency,
        
        // Labor
        hourly_labor_rate: labor.rate_per_hour,
        crew_size: labor.crew_size,
        laborersOnSite: labor.crew_size,
        
        // Calculated values (validated)
        bid_amount: validatedBidAmount,
        noOfDaysExpected: validatedDays,
        
        // Time & Material
        time_material_enabled: manHour.enabled,
        time_material_rate: manHour.bill_rate_per_hour,
        
        // Janitorial
        janitorial_enabled: janitorial.enabled,
        janitorial_monthly_rate: validatedJanitorialMonthly,
        
        window_panes: calc.windowPanesComputed || 0,
        price_per_window: calc.windowPanesComputed > 0 
          ? (calc.itemizedModelPost.windows / calc.windowPanesComputed) 
          : 0,
        pressure_wash: pressure.area,
        pressure_wash_price: pressure.area > 0 
          ? (calc.itemizedModelPost.pressure / pressure.area)
          : 0,
        
        // Complete state as JSON
        calculator_data_json: JSON.stringify(calculatorState)
      };
    } catch (error) {
      console.error('Validation error in getEstimatePayload:', error);
      toastError(error.message);
      throw error;
    }
  };

  const loadEstimateData = (savedData) => {
    try {
      if (savedData.calculator_data_json) {
        const calculatorState = JSON.parse(savedData.calculator_data_json);
        deserializeCalculatorState(calculatorState);
      } else {
        if (savedData.project_name) setEstimateName(savedData.project_name);
        if (savedData.notes) setEstimateDesc(savedData.notes);
        if (savedData.country) setCountry(savedData.country);
        if (savedData.state) setStateProvince(savedData.state);
        if (savedData.square_foot) setBuilding(prev => ({ ...prev, area: parseFloat(savedData.square_foot) }));
        if (savedData.building_levels) setBuilding(prev => ({ ...prev, levels: parseInt(savedData.building_levels) }));
        if (savedData.phase_count) setPhaseCount(savedData.phase_count);
        if (savedData.single_phase) setSinglePhase(savedData.single_phase);
        if (savedData.two_phase_combo) setTwoPhaseCombo(savedData.two_phase_combo);
        if (savedData.project_type) setProjectType(savedData.project_type);
        if (savedData.selected_model) setSelectedModel(savedData.selected_model);
        if (savedData.units_type) setUnits(savedData.units_type);
        if (savedData.currency_type) setCurrency(savedData.currency_type);
        if (savedData.hourly_labor_rate) setLabor(prev => ({ ...prev, rate_per_hour: parseFloat(savedData.hourly_labor_rate) }));
        if (savedData.crew_size) setLabor(prev => ({ ...prev, crew_size: parseInt(savedData.crew_size) }));
        if (savedData.time_material_enabled !== undefined) setManHour(prev => ({ ...prev, enabled: savedData.time_material_enabled }));
        if (savedData.time_material_rate) setManHour(prev => ({ ...prev, bill_rate_per_hour: parseFloat(savedData.time_material_rate) }));
        if (savedData.janitorial_enabled !== undefined) setJanitorial(prev => ({ ...prev, enabled: savedData.janitorial_enabled }));
        if (savedData.window_panes) setWindows(prev => ({ ...prev, panes: parseFloat(savedData.window_panes) }));
        if (savedData.price_per_window) setWindows(prev => ({ ...prev, price_per_pane: parseFloat(savedData.price_per_window) }));
        if (savedData.pressure_wash) setPressure(prev => ({ ...prev, area: parseFloat(savedData.pressure_wash) }));
        if (savedData.pressure_wash_price) setPressure(prev => ({ ...prev, rate_per_unit: parseFloat(savedData.pressure_wash_price) }));
      }
    } catch (error) {
      console.error('Error loading estimate:', error);
    }
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    getEstimatePayload,
    loadEstimateData
  }));

  // ----------------- UI -----------------
  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          {/* Header */}
          <Row className="mb-4">
            <Col md={6} className="d-flex align-items-center">
              <Button 
                variant={activeKeys.length >= 5 ? "primary" : "outline-primary"}
                size="lg"
                onClick={() => {
                  const allKeys = ['project', 'phases', 'pricing', 'operations', 'calculation', 'windows', 'unitsBaths', 'unitsMix', 'pressure', 'flooring', 'janitorial', 'proposalExport'];
                  const mostCollapsed = activeKeys.length < allKeys.length / 2;
                  setActiveKeys(mostCollapsed ? allKeys : []);
                }}
              >
                {activeKeys.length < 5 ? 'Expand All' : 'Collapse All'}
              </Button>
              <InputGroup className="d-inline-flex w-auto gap-2 ms-2">
                <Form.Select value={units} onChange={(e)=>handleUnitsChange(e.target.value)}>
                  <option value="ft">Feet²</option>
                  <option value="m">Meters²</option>
                </Form.Select>
                <Form.Select value={currency} onChange={(e)=>handleCurrencyChange(e.target.value)} size="sm">
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                </Form.Select>
              </InputGroup>
            </Col>
            <Col md={6} className="text-end">
              <div className="mt-2">
                <Form.Label className="text-muted small w-100">Example scenarios</Form.Label>
                <Form.Select 
                  value={scenario} 
                  onChange={(e)=>{ const v=e.target.value; setScenario(v); applyScenario(v); }}
                  size="sm"
                >
                  <option value="">— Select —</option>
                  <option value="warehouse100k_final_preset">100,000 ft² Warehouse — Final only + Around-building PW</option>
                  <option value="three_bldgs_mixed_sizes">3 buildings (6k, 9k, 5k ft²), TX — Rough+Final + Windows</option>
                  <option value="corp_30k_10lvls_windows">Corporate HQ — 30,000 ft² × 10 levels — Windows</option>
                  <option value="apt_unitsmix_120">Apartment Complex — Units Mix: 120 units (60×1BR, 40×2BR, 20×3BR)</option>
                  <option value="janitorial_office_25k_5x">Recurring Janitorial — Office 25,000 ft² — 5×/week (monthly)</option>
                  <option value="house_oh_5br_3ba_2story_windows">Ohio Residential — 5 bedrooms, 3 baths, 2 stories + Windows</option>
                </Form.Select>
              </div>
            </Col>
          </Row>

          {/* Row 1: Estimate meta */}
          <Row className="mb-4">
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted w-100">Estimate name</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder="e.g., Final Clean — Project / Client" 
                  value={estimateName} 
                  onChange={(e)=>setEstimateName(e.target.value)} 
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="small text-muted w-100">Description / notes</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={4} 
                  placeholder="Scope notes, site constraints, inclusions/exclusions..." 
                  value={estimateDesc} 
                  onChange={(e)=>setEstimateDesc(e.target.value)} 
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Row 2: Location */}
          <Card className="mb-4">
            {/* <Card.Header> */}
              <Card.Title className="mb-0 h4">Location</Card.Title>
            {/* </Card.Header> */}
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted w-100">Country</Form.Label>
                    <Form.Select 
                      value={country} 
                      onChange={(e)=>handleCountryChange(e.target.value)}
                    >
                      {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted w-100">State / Province</Form.Label>
                    <Form.Select value={stateProvince} onChange={(e)=>handleStateChange(e.target.value)}>
                      {STATE_PROVINCE_OPTIONS[country].map((s) => <option key={s} value={s}>{s}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              {/* <Form.Check
                type="checkbox"
                id="autoLabor"
                label={`Auto from ${stateProvince}`}
                checked={autoLabor}
                onChange={(e)=>setAutoLabor(e.target.checked)}
                className="small"
              /> */}
            </Card.Body>
          </Card>

          {/* Project */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="project">
              <Accordion.Header className="bg-light">
                <div className="d-flex align-items-center">
                  <i className="fas fa-building me-2 text-primary"></i>
                  <span className="fw-bold">Project</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-3">
              {/* Row 1: Project Type + Building Area */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted fw-bold w-100">Project Type</Form.Label>
                    <Form.Select 
                      value={projectType} 
                      onChange={(e)=>handleProjectTypeChange(e.target.value)}
                      size="sm"
                    >
                      {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted fw-bold w-100">Total Area ({unitAreaLabel})</Form.Label>
                    <Form.Control 
                      type="number" 
                      size="sm"
                      value={building.area} 
                      onChange={(e)=> handleBuildingAreaChange(Math.max(0, parseFloat(e.target.value)||0)) } 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted fw-bold w-100">Levels / Stories</Form.Label>
                    <Form.Control 
                      type="number" 
                      min={1} 
                      size="sm"
                      value={building.levels} 
                      onChange={(e)=>setBuilding({ ...building, levels: Math.max(1, parseInt(e.target.value||'1',10)) })} 
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Detailed Buildings Toggle */}
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Check
                    type="checkbox"
                    id="useDetailed"
                    label="Use detailed per-building sizes"
                    checked={building.useDetailed}
                    onChange={(e)=>setBuilding(prev => ({ ...prev, useDetailed: e.target.checked }))}
                    className="small fw-bold"
                  />
                </Col>
              </Row>

              {building.useDetailed && (
                <div className="mt-3">
                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Check
                        type="checkbox"
                        id="autoArea"
                        label="Auto total area from list"
                        checked={building.autoArea}
                        onChange={(e)=>setBuilding(prev=>({ ...prev, autoArea: e.target.checked }))}
                        className="small"
                      />
                    </Col>
                    <Col md={6} className="text-end">
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={()=>setBuilding(prev=>({ ...prev, details: [...prev.details, { name: `Bldg ${String.fromCharCode(65 + prev.details.length)}` , area: 0 }] }))}
                      >
                        + Add building
                      </Button>
                    </Col>
                  </Row>
                  <Table responsive size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Building</th>
                        <th>Area ({unitAreaLabel})</th>
                        <th width="100"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {building.details.map((d, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Control 
                              type="text" 
                              size="sm"
                              value={d.name} 
                              onChange={(e)=>setBuilding(prev=>({ ...prev, details: prev.details.map((dd,i)=> i===idx ? { ...dd, name: e.target.value } : dd) }))} 
                            />
                          </td>
                          <td>
                            <Form.Control 
                              type="number" 
                              size="sm"
                              placeholder="(unknown)" 
                              value={d.area} 
                              onChange={(e)=>setBuilding(prev=>({ ...prev, details: prev.details.map((dd,i)=> i===idx ? { ...dd, area: Math.max(0, parseFloat(e.target.value)||0) } : dd) }))} 
                            />
                          </td>
                          <td>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={()=>setBuilding(prev=>({ ...prev, details: prev.details.filter((_,i)=>i!==idx) }))}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <small className="text-muted">
                    Known total: <strong>{fmt0(building.details.reduce((s,d)=> s + (Number(d.area)||0), 0))}</strong> {unitAreaLabel} • Unknown entries count as 0 until filled.
                  </small>
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
          </Accordion>

          {/* Phases */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="phases">
              <Accordion.Header className="bg-light">
                <div className="d-flex align-items-center">
                  <i className="fas fa-tasks me-2 text-success"></i>
                  <span className="fw-bold">Phases</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-3">
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted fw-bold w-100">Phase Count</Form.Label>
                    <Form.Select 
                      size="sm"
                      value={phaseCount} 
                      onChange={(e)=>setPhaseCount(Number(e.target.value))}
                    >
                      <option value={1}>1 Phase</option>
                      <option value={2}>2 Phases</option>
                      <option value={3}>3 Phases</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {phaseCount === 1 && (
                <div className="mb-3">
                  <Form.Label className="small text-muted fw-bold w-100">Single Phase Type:</Form.Label>
                  <div className="d-flex flex-row gap-5 mt-2">
                    <Form.Check
                      type="radio"
                      name="singlePhase"
                      value="rough"
                      id="singleRough"
                      label="Rough Clean"
                      checked={singlePhase==='rough'}
                      onChange={()=>setSinglePhase('rough')}
                      className="small"
                    />
                    <Form.Check
                      type="radio"
                      name="singlePhase"
                      value="final"
                      id="singleFinal"
                      label="Final Clean"
                      checked={singlePhase==='final'}
                      onChange={()=>setSinglePhase('final')}
                      className="small"
                    />
                    <Form.Check
                      type="radio"
                      name="singlePhase"
                      value="fluff"
                      id="singleFluff"
                      label="Fluff Clean"
                      checked={singlePhase==='fluff'}
                      onChange={()=>setSinglePhase('fluff')}
                      className="small ms-0"
                    />
                  </div>
                </div>
              )}

              {phaseCount === 2 && (
                <div className="mb-3">
                  <Form.Group className="mb-3">
                    <Form.Label className="small text-muted fw-bold w-100">Two-Phase Combo:</Form.Label>
                    <Form.Select 
                      size="sm"
                      value={twoPhaseCombo} 
                      onChange={(e)=>setTwoPhaseCombo(e.target.value)}
                    >
                      <option value="rough_final">Rough + Final (35/65)</option>
                      <option value="final_fluff">Final + Fluff (70/30)</option>
                      <option value="rough_fluff">Rough + Fluff (60/40)</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              )}

              <hr className="my-3" />
              <div className="mb-3">
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Rough (%)</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={weights.rough} 
                        onChange={(e)=>setWeights({ ...weights, rough: clamp(parseFloat(e.target.value)||0, 0, 100) })} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Final (%)</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={weights.final} 
                        onChange={(e)=>setWeights({ ...weights, final: clamp(parseFloat(e.target.value)||0, 0, 100) })} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Fluff (%)</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={weights.fluff} 
                        onChange={(e)=>setWeights({ ...weights, fluff: clamp(parseFloat(e.target.value)||0, 0, 100) })} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <div className="mb-3">
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Rough passes</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={phasePasses.rough} 
                        onChange={(e)=>setPhasePasses({ ...phasePasses, rough: Math.max(1, parseInt(e.target.value||'1',10)) })} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Final passes</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={phasePasses.final} 
                        onChange={(e)=>setPhasePasses({ ...phasePasses, final: Math.max(1, parseInt(e.target.value||'1',10)) })} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Fluff passes</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={phasePasses.fluff} 
                        onChange={(e)=>setPhasePasses({ ...phasePasses, fluff: Math.max(1, parseInt(e.target.value||'1',10)) })} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {building.area != 0 && !unitsBaths.addToBid &&
                <Form.Check
                  type="checkbox"
                  id="showPhaseBreakout"
                  label="Show phase breakdown in Selected Bid"
                  checked={showPhaseBreakout}
                  onChange={(e)=>setShowPhaseBreakout(e.target.checked)}
                  className="small mb-3"
                />
              }

              {/* Phase Details */}
              <hr className="my-3" />
              <div className="mb-3 text-start">
                <h6 className="small text-muted mb-2">Phase Details</h6>
                <Row>
                  {activePhases.rough && (
                    <Col md={4}>
                      <Card className="mb-2 p-3 bg-light">
                        <Card.Title className="small mb-1">Rough</Card.Title>
                        <div className="small">
                          <div>Hours: {fmt0(calc.roughHrs)} ({fmt0(calc.roughHrs / Math.max(1, phasePasses.rough))} × {phasePasses.rough})</div>
                          <div>Cost: {fmt(calc.roughHrs * labor.rate_per_hour, currency)}</div>
                          <div>Per pass: {fmt((calc.roughHrs / Math.max(1, phasePasses.rough)) * labor.rate_per_hour, currency)}</div>
                        </div>
                      </Card>
                    </Col>
                  )}
                  {activePhases.final && (
                    <Col md={4}>
                      <Card className="mb-2 p-3 bg-light">
                        <Card.Title className="small mb-1">Final</Card.Title>
                        <div className="small">
                          <div>Hours: {fmt0(calc.finalHrs)} ({fmt0(calc.finalHrs / Math.max(1, phasePasses.final))} × {phasePasses.final})</div>
                          <div>Cost: {fmt(calc.finalHrs * labor.rate_per_hour, currency)}</div>
                          <div>Per pass: {fmt((calc.finalHrs / Math.max(1, phasePasses.final)) * labor.rate_per_hour, currency)}</div>
                        </div>
                      </Card>
                    </Col>
                  )}
                  {activePhases.fluff && (
                    <Col md={4}>
                      <Card className="mb-2 p-3 bg-light">
                        <Card.Title className="small mb-1">Fluff</Card.Title>
                        <div className="small">
                          <div>Hours: {fmt0(calc.fluffHrs)} ({fmt0(calc.fluffHrs / Math.max(1, phasePasses.fluff))} × {phasePasses.fluff})</div>
                          <div>Cost: {fmt(calc.fluffHrs * labor.rate_per_hour, currency)}</div>
                          <div>Per pass: {fmt((calc.fluffHrs / Math.max(1, phasePasses.fluff)) * labor.rate_per_hour, currency)}</div>
                        </div>
                      </Card>
                    </Col>
                  )}
                </Row>
                <small className="text-muted">
                  Total: {fmt0(calc.laborHours)} hours • {fmt(calc.laborCost, currency)} • Phase factor: {calc.phaseFactor.toFixed(2)}×
                </small>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          </Accordion>
          
          {/* Pricing */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="pricing">
              <Accordion.Header className="bg-light">
                <div className="d-flex align-items-center">
                  <i className="fas fa-dollar-sign me-2 text-warning"></i>
                  <span className="fw-bold">Pricing</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-3">
              <div className="mb-4 text-start">
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Labor Rate (USD/hr)</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={labor.rate_per_hour} 
                        onChange={(e)=>handleLaborRateChange(Math.max(0, parseFloat(e.target.value)||0))} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Crew Size</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={labor.crew_size} 
                        onChange={(e)=>handleCrewSizeChange(Math.max(1, parseInt(e.target.value||'1',10)))} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="p-2 text-start">
                      <div className="small text-muted mb-1">Effective Rate</div>
                      <div className="h4 fw-bold mb-1">{fmtRate(calc.effectiveSqftRate, currency, 3)}/ft²</div>
                      <div className="small text-muted w-100">
                        <span>Loc: {calc.locRateMult.toFixed(2)}×</span><span className="mx-2">•</span>
                        <span>Type: {calc.typeRateMult.toFixed(2)}×</span><span className="mx-2">•</span>
                        <span>Phase: {calc.phaseRateMult.toFixed(2)}×</span>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">National Base (seed)</Form.Label>
                      <Form.Control 
                        type="number" 
                        step="0.01" 
                        size="sm"
                        value={nationalBaseSqft} 
                        onChange={(e)=>handleNationalBaseChange(Math.max(0, parseFloat(e.target.value)||0))} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Special Labor Requirements - Nested Accordion */}
              <Accordion className="mb-3">
                <Accordion.Item eventKey="wage-requirements">
                  <Accordion.Header>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-briefcase me-2 text-info"></i>
                      <span className="fw-bold">Special Labor Requirements (Davis Bacon / Union)</span>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body className="p-3">
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          id="davisChecked"
                          label="Davis-Bacon Prevailing Wage"
                          checked={davis.checked}
                          onChange={(e)=>setDavis(prev=>({ ...prev, checked: e.target.checked }))}
                          className="small fw-bold"
                        />
                      </Col>
                      {davis.checked && (
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="small text-muted fw-bold w-100">Increase %</Form.Label>
                            <Form.Control 
                              type="number" 
                              size="sm"
                              value={davis.percentage} 
                              onChange={(e)=> handleDavisPercentageChange(Math.max(0, parseFloat(e.target.value)||0)) } 
                            />
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          id="unionChecked"
                          label="Union Requirement"
                          checked={union.checked}
                          onChange={(e)=>setUnion(prev=>({ ...prev, checked: e.target.checked }))}
                          className="small fw-bold"
                        />
                      </Col>
                      {union.checked && (
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="small text-muted fw-bold w-100">Increase %</Form.Label>
                            <Form.Control 
                              type="number" 
                              size="sm"
                              value={union.percentage} 
                              onChange={(e)=> handleUnionPercentageChange(Math.max(0, parseFloat(e.target.value)||0)) } 
                            />
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Accordion.Body>
          </Accordion.Item>
          </Accordion>
          
          {/* Operations */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="operations">
              <Accordion.Header className="bg-light">
                <div className="d-flex align-items-center">
                  <i className="fas fa-dollar-sign me-2 text-warning"></i>
                  <span className="fw-bold">Operations</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-3">

              {/* T&M Section */}
              <div className="mb-4 text-start">
                <h6 className="small text-muted fw-bold mb-3">
                  <i className="fas fa-clock me-1"></i>
                  Time & Material (T&M)
                </h6>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Bill Rate (USD/hr)</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={manHour.bill_rate_per_hour} 
                        onChange={(e)=>handleBillRateChange(Math.max(0, parseFloat(e.target.value)||0))} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mt-4">
                    <Form.Check
                      type="checkbox"
                      id="manHourOverride"
                      label="Override hours manually"
                      checked={manHour.override}
                      onChange={(e)=>setManHour(prev=>({ ...prev, override: e.target.checked }))}
                      className="small fw-bold mt-4"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Manual Hours</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        disabled={!manHour.override}
                        value={manHour.manual_hours} 
                        onChange={(e)=>setManHour({ ...manHour, manual_hours: Math.max(0, parseFloat(e.target.value)||0) })} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="small text-muted mb-1">T&M Total</div>
                    <div className="h4 fw-bold mb-0">{fmt(calc.manHourTotal, currency)}</div>
                  </Col>
                  <Col md={6}>
                    <div className="small text-muted mt-2">
                      {fmt0(manHour.override ? manHour.manual_hours : calc.laborHours)} hrs × {fmtRate(manHour.bill_rate_per_hour, currency)}/hr + add-ons
                    </div>
                  </Col>
                  <Col md={6} className="mt-3">
                    <Form.Check
                      type="checkbox"
                      id="manHourApplyToSelected"
                      label="Use T&M as Selected Bid"
                      checked={manHour.apply_to_selected}
                      onChange={(e)=>setManHour(prev=>({ ...prev, apply_to_selected: e.target.checked }))}
                      className="small fw-bold"
                    />
                  </Col>
                </Row>
              </div>

              {/* Travel Section */}
              <hr className="my-3" />
              <div className="mb-3 text-start">
                <h6 className="small text-muted fw-bold mb-3">
                  <i className="fas fa-route me-1"></i>
                  Travel / Site Visits
                </h6>
                <Form.Check
                  type="checkbox"
                  id="travelAddToBid"
                  label="Add to bid"
                  checked={travel.addToBid}
                  onChange={(e)=>setTravel(prev=>({ ...prev, addToBid: e.target.checked }))}
                  className="small fw-bold mb-3"
                />
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Check
                      type="checkbox"
                      id="travelAuto"
                      label="Auto visits from phases/days"
                      checked={travel.auto}
                      onChange={(e)=>setTravel(prev=>({ ...prev, auto: e.target.checked }))}
                      className="small fw-bold mt-3"
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Visits</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        disabled={travel.auto}
                        value={travel.visits} 
                        onChange={(e)=>setTravel({ ...travel, visits: Math.max(0, parseInt(e.target.value||'0',10)) })} 
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Cost per additional visit</Form.Label>
                      <Form.Control 
                        type="number" 
                        size="sm"
                        value={travel.cost_per_visit} 
                        onChange={(e)=>setTravel({ ...travel, cost_per_visit: Math.max(0, parseFloat(e.target.value)||0) })} 
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="small text-muted text-start">
                  <i className="fas fa-info-circle me-2"></i>
                  First 2 visits are included; billing starts after 2. Travel total: <strong>{fmt(calc.travelCost, currency)}</strong> = {Math.max(0, travel.visits - 2)} billed visits × {fmt(travel.cost_per_visit, currency)}
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          </Accordion>

          {/* Calculation Details */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="calculation">
              <Accordion.Header className="bg-light">
                <div className="d-flex align-items-center">
                  <i className="fas fa-dollar-sign me-2 text-warning"></i>
                  <span className="fw-bold">Calculation Details</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-3">
              {/* Calculation Details */}
              <hr className="my-3" />
              <div className="mb-4 text-start">
                <h6 className="small text-muted fw-bold mb-3">
                  <i className="fas fa-info-circle me-1"></i>
                  Calculation Details
                </h6>
                <Row>
                  <Col md={6}>
                    <div className="p-2 rounded mb-2">
                      <div className="small fw-bold text-dark mb-2">Multipliers</div>
                      <div className="small text-muted">
                        <div>Location: <strong>{calc.locRateMult.toFixed(2)}×</strong></div>
                        <div>Type: <strong>{calc.typeRateMult.toFixed(2)}×</strong></div>
                        <div>Phase: <strong>{calc.phaseRateMult.toFixed(2)}×</strong></div>
                        <div>Level: <strong>{calc.windowLevelFactor.toFixed(2)}×</strong></div>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="p-2 rounded mb-2">
                      <div className="small fw-bold text-dark mb-2">Productivity (m²/hr)</div>
                      <div className="small text-muted">
                        <div>Rough: <strong>{fmt0(effProd.rough_m2_hr)}</strong></div>
                        <div>Final: <strong>{fmt0(effProd.final_m2_hr)}</strong></div>
                        <div>Fluff: <strong>{fmt0(effProd.fluff_m2_hr)}</strong></div>
                      </div>
                    </div>
                  </Col>
                </Row>
                <div className="d-flex align-items-center">
                  <i className="fas fa-chart-area me-2"></i>
                  <div className="small text-muted">
                    Area: {fmt0(building.area)} {unitAreaLabel} ({fmt0(calc.areaM2)} m², {fmt0(calc.areaFt2)} ft²) • 
                    Days: {fmt0(calc.days)} @ 8hrs/day
                  </div>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
          </Accordion>
          
          {/* Windows Section */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="windows">
              <Accordion.Header>
              <span className="fw-bold">Windows</span>
              </Accordion.Header>
              <Accordion.Body>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Check
                  type="checkbox"
                  id="windowsAddToBid"
                  label="Add to bid"
                  checked={windows.addToBid}
                  onChange={(e)=>setWindows(prev=>({ ...prev, addToBid: e.target.checked }))}
                />
              </Col>
              <Col md={6} className="text-end">
                <div bg="success" className="fs-4 fw-bold text-success">
                  Total: {fmt(calc.windowCost, currency)}
                </div>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small text-muted w-100">Mode</Form.Label>
                  <Form.Select 
                    value={windows.mode} 
                    onChange={(e)=>setWindows({ ...windows, mode: e.target.value })}
                  >
                    <option value="auto">Auto (by ft² × levels)</option>
                    <option value="panes">Manual panes</option>
                    <option value="units">By units</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {windows.mode === 'panes' && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small text-muted w-100">Panes</Form.Label>
                    <Form.Control 
                      type="number"
                      value={windows.panes}
                      onChange={(e)=>setWindows({ ...windows, panes: Math.max(0, parseInt(e.target.value||'0',10)) })}
                    />
                  </Form.Group>
                </Col>
              )}
              {windows.mode === 'units' && (
                <>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Units</Form.Label>
                      <Form.Control 
                        type="number"
                        value={windows.units_count}
                        onChange={(e)=>setWindows({ ...windows, units_count: Math.max(0, parseInt(e.target.value||'0',10)) })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Panes per unit</Form.Label>
                      <Form.Control 
                        type="number"
                        value={windows.panes_per_unit}
                        onChange={(e)=>setWindows({ ...windows, panes_per_unit: Math.max(0, parseInt(e.target.value||'0',10)) })}
                      />
                    </Form.Group>
                  </Col>
                </>
              )}
              {windows.mode === 'auto' && (
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="small text-muted w-100">Panes / 1000 ft²</Form.Label>
                    <Form.Control 
                      type="number"
                      value={windows.density_per_1000ft2}
                      onChange={(e)=>setWindows({ ...windows, density_per_1000ft2: Math.max(0, parseFloat(e.target.value)||0) })}
                    />
                  </Form.Group>
                </Col>
              )}
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small text-muted w-100">Price per pane</Form.Label>
                  <Form.Control 
                    type="number"
                    value={windows.price_per_pane}
                    onChange={(e)=>setWindows({ ...windows, price_per_pane: Math.max(0, parseFloat(e.target.value)||0) })}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Pane Calculation Details */}
            <Card className="mt-3 bg-light text-start px-3 py-2">
              <Card.Body className="p-3">
                <h6 className="fw-bold text-dark mb-3">Pane Calculation Details</h6>
                <div className="small text-muted">
                  {windows.mode === 'auto' && (
                    <div>
                      <div className="fw-bold mb-1">Auto Mode Calculation:</div>
                      <div>Building area: {fmt0(calc.areaFt2)} ft² × {building.levels} levels</div>
                      <div>Density: {windows.density_per_1000ft2} panes per 1,000 ft²</div>
                      <div>Formula: ({fmt0(calc.areaFt2)} ÷ 1000) × {windows.density_per_1000ft2} × {building.levels} = {fmt0(calc.windowPanesComputed)} panes</div>
                    </div>
                  )}
                  {windows.mode === 'units' && (
                    <div>
                      <div className="fw-bold mb-1">Units Mode Calculation:</div>
                      <div>{windows.units_count} units × {windows.panes_per_unit} panes/unit = {fmt0(calc.windowPanesComputed)} panes</div>
                    </div>
                  )}
                  {windows.mode === 'panes' && (
                    <div>
                      <div className="fw-bold mb-1">Manual Mode:</div>
                      <div>Direct entry: {fmt0(calc.windowPanesComputed)} panes</div>
                    </div>
                  )}
                  <hr className="my-2" />
                  <div>Level penalty multiplier: {calc.windowLevelFactor.toFixed(2)}× (3% per level above 1st)</div>
                  <div>Effective price per pane: {fmtRate(windows.price_per_pane * calc.windowLevelFactor, currency)}</div>
                  <div className="fw-bold mt-2">
                    Base window cost: {fmt(calc.windowCost - calc.liftCost, currency)} = {fmt0(calc.windowPanesComputed)} panes × {fmtRate(windows.price_per_pane * calc.windowLevelFactor, currency)}
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Lift Equipment Section */}
            <Card className="mt-3 text-start p-3">
              <Card.Title className="mb-0 ms-2 h4">Lift Equipment</Card.Title>
              <Card.Body>
                <Row>
                  <Col md={4} style={{ alignSelf: 'anchor-center' }}>
                    <Form.Check
                      type="checkbox"
                      id="includeLift"
                      label="Include lift"
                      checked={windows.include_lift}
                      onChange={(e)=>setWindows(prev=>({ ...prev, include_lift: e.target.checked }))}
                    />
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Daily cost</Form.Label>
                      <Form.Control 
                        type="number"
                        value={windows.lift_cost_daily}
                        onChange={(e)=>setWindows({ ...windows, lift_cost_daily: Math.max(0, parseFloat(e.target.value)||0) })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label className="small text-muted w-100">Days needed</Form.Label>
                      <Form.Control 
                        type="number"
                        value={windows.lift_days}
                        onChange={(e)=>setWindows({ ...windows, lift_days: Math.max(0, parseInt(e.target.value||'0',10)) })}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <div className="small mt-2 text-muted">
                  {building.levels > 2 && (
                    <div className="text-warning fw-bold mb-1">⚠ Auto-included: 3+ story buildings require lift equipment</div>
                  )}
                  <div>Suggested daily rate for {stateProvince}: {fmt(getAutoLiftDaily(stateProvince), currency)}</div>
                  <div>Auto days calculation: {defaultLiftDaysFromVisits(travel.visits)} days (based on {travel.visits} site visits)</div>
                  {nz(calc.liftCost) && (
                    <div className="text-success fw-bold mt-1">
                      Lift total: {fmt(calc.liftCost, currency)} ({fmt(windows.lift_cost_daily || Math.round(getAutoLiftDaily(stateProvince)), currency)} × {windows.lift_days || defaultLiftDaysFromVisits(travel.visits)} days)
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Accordion.Body>
        </Accordion.Item>
          </Accordion>

          {/* Simple Rooms/Units Section */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="unitsBaths">
              <Accordion.Header>
                <span className="fw-bold">Simple Rooms / Units</span>
              </Accordion.Header>
              <Accordion.Body>
                <Form.Check
                  type="checkbox"
                  id="unitsBathsAddToBid"
                  label="Add to bid"
                  checked={unitsBaths.addToBid}
                  onChange={(e)=>setUnitsBaths(prev=>({ ...prev, addToBid: e.target.checked }))}
                  className="mb-3"
                />

                <Row>
                  <Col md={6} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label className="small text-muted w-100">Units × Rate</Form.Label>
                      <Row>
                        <Col>
                          <Form.Control 
                            type="number"
                            value={unitsBaths.units}
                            onChange={(e)=>setUnitsBaths({ ...unitsBaths, units: Math.max(0, parseInt(e.target.value||'0',10)), userEdited: true })}
                            placeholder="Units"
                          />
                        </Col>
                        <Col>
                          <Form.Control 
                            type="number"
                            value={unitsBaths.rate_per_unit}
                            onChange={(e)=>setUnitsBaths({ ...unitsBaths, rate_per_unit: Math.max(0, parseFloat(e.target.value)||0), userEdited: true })}
                            placeholder="Rate"
                          />
                        </Col>
                      </Row>
                      <Form.Text className="text-muted text-start">Suggested: {fmtRate(simpleSuggest.unit, currency, 0)}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6} className="text-start">
                    <Form.Group className="mb-3">
                      <Form.Label className="small text-muted w-100">Baths × Rate</Form.Label>
                      <Row>
                        <Col>
                          <Form.Control 
                            type="number"
                            step="0.5"
                            value={unitsBaths.baths}
                            onChange={(e)=>setUnitsBaths({ ...unitsBaths, baths: Math.max(0, parseFloat(e.target.value)||0), userEdited: true })}
                            placeholder="Baths"
                          />
                        </Col>
                        <Col>
                          <Form.Control 
                            type="number"
                            value={unitsBaths.rate_per_bath}
                            onChange={(e)=>setUnitsBaths({ ...unitsBaths, rate_per_bath: Math.max(0, parseFloat(e.target.value)||0), userEdited: true })}
                            placeholder="Rate"
                          />
                        </Col>
                      </Row>
                      <Form.Text className="text-muted">Suggested: {fmtRate(simpleSuggest.bath, currency, 0)}</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="small text-muted text-start">
                  Current: {fmt0(unitsBaths.units)} × {fmtRate(unitsBaths.rate_per_unit, currency, 0)} + {fmt0(unitsBaths.baths)} × {fmtRate(unitsBaths.rate_per_bath, currency, 0)} = <strong>{fmt(calc.unitsBathsTotalRaw, currency)}</strong>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Units Mix Section */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="unitsMix">
              <Accordion.Header>
                <span className="fw-bold">Units Mix</span>
              </Accordion.Header>
              <Accordion.Body>
                <Form.Check
                  type="checkbox"
                  id="unitsMixAddToBid"
                  label="Add to bid"
                  checked={unitsMix.addToBid}
                  onChange={(e)=>setUnitsMix(prev=>({ ...prev, addToBid: e.target.checked }))}
                  className="mb-3"
                />

                <Table responsive size="sm" bordered>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Sqft</th>
                      <th>Base</th>
                      <th>/Bath</th>
                      <th>1-Bath</th>
                      <th>2-Bath</th>
                      <th>3-Bath</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitsMix.rows.map((r, idx) => (
                      <tr key={idx}>
                        <td>{r.type}</td>
                        <td>
                          <Form.Control 
                            type="number"
                            size="sm"
                            value={r.sqft || 0}
                            onChange={(e)=>{
                              const newSqft = Math.max(0, parseInt(e.target.value||'0',10));
                              setUnitsMix(prev=>({ ...prev, rows: prev.rows.map((rr,i)=> {
                                if (i !== idx) return rr;
                                const defaultSqft = [500, 750, 1000, 1250, 1500, 1750][i] || 1000;
                                const defaultBaseRates = [200, 250, 300, 350, 400, 450];
                                const defaultBaseRate = defaultBaseRates[i] || 300;
                                const scaleFactor = newSqft > 0 ? newSqft / defaultSqft : 1;
                                const scaledBaseRate = Math.round(defaultBaseRate * scaleFactor);
                                return { ...rr, sqft: newSqft, baseRate: scaledBaseRate, baseRateUnscaled: scaledBaseRate };
                              }) }));
                            }}
                          />
                        </td>
                        <td>
                          <Form.Control 
                            type="number"
                            size="sm"
                            value={r.baseRate}
                            onChange={(e)=>setUnitsMix(prev=>({ ...prev, rows: prev.rows.map((rr,i)=> i===idx ? { ...rr, baseRate: Math.max(0, parseFloat(e.target.value)||0) } : rr) }))}
                          />
                        </td>
                        <td>
                          <Form.Control 
                            type="number"
                            size="sm"
                            value={r.ratePerBath}
                            onChange={(e)=>setUnitsMix(prev=>({ ...prev, rows: prev.rows.map((rr,i)=> i===idx ? { ...rr, ratePerBath: Math.max(0, parseFloat(e.target.value)||0) } : rr) }))}
                          />
                        </td>
                        <td>
                          <Form.Control 
                            type="number"
                            size="sm"
                            value={r.units1Bath}
                            onChange={(e)=>setUnitsMix(prev=>({ ...prev, rows: prev.rows.map((rr,i)=> i===idx ? { ...rr, units1Bath: Math.max(0, parseInt(e.target.value||'0',10)) } : rr) }))}
                          />
                        </td>
                        <td>
                          <Form.Control 
                            type="number"
                            size="sm"
                            value={r.units2Bath}
                            onChange={(e)=>setUnitsMix(prev=>({ ...prev, rows: prev.rows.map((rr,i)=> i===idx ? { ...rr, units2Bath: Math.max(0, parseInt(e.target.value||'0',10)) } : rr) }))}
                          />
                        </td>
                        <td>
                          <Form.Control 
                            type="number"
                            size="sm"
                            value={r.units3Bath}
                            onChange={(e)=>setUnitsMix(prev=>({ ...prev, rows: prev.rows.map((rr,i)=> i===idx ? { ...rr, units3Bath: Math.max(0, parseInt(e.target.value||'0',10)) } : rr) }))}
                          />
                        </td>
                        <td className="text-end text-success">
                          <strong>{fmt(calc.unitsMixRowTotals[idx]?.rowTotal || 0, currency)}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <p className="text-start text-muted">Grand Total: <strong>{fmt(calc.unitsMixRowTotals.reduce((sum, rt) => sum + (rt?.rowTotal || 0), 0), currency)}</strong></p>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Pressure Wash Section */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="pressure">
              <Accordion.Header>
                <span className="fw-bold">Pressure Wash</span>
              </Accordion.Header>
              <Accordion.Body>
                <Form.Check
                  type="checkbox"
                  id="pressureAddToBid"
                  label="Add to bid"
                  checked={pressure.addToBid}
                  onChange={(e)=>setPressure(prev=>({ ...prev, addToBid: e.target.checked }))}
                  className="mb-3"
                />

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small text-muted w-100">Preset</Form.Label>
                      <Form.Select 
                        value={pressure.preset}
                        onChange={(e)=>setPressure({ ...pressure, preset: e.target.value })}
                      >
                        <option value="manual">Off / Manual</option>
                        <option value="around">Around building only</option>
                        <option value="sidewalks_pads">Sidewalks + entrances + pads</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small text-muted w-100">Area (ft²)</Form.Label>
                      <Form.Control 
                        type="number"
                        value={pressure.area}
                        onChange={(e)=>setPressure({ ...pressure, area: Math.max(0, parseFloat(e.target.value)||0) })}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted w-100">Rate per unit</Form.Label>
                  <Form.Control 
                    type="number"
                    step="0.01"
                    value={pressure.rate_per_unit}
                    onChange={(e)=>setPressure({ ...pressure, rate_per_unit: Math.max(0, parseFloat(e.target.value)||0) })}
                  />
                </Form.Group>

                <div className="small text-muted text-start">
                  Pressure wash total: <strong>{fmt(calc.pressureCost, currency)}</strong>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Special Flooring Section */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="flooring">
              <Accordion.Header>
                <span className="fw-bold">Special Flooring</span>
              </Accordion.Header>
              <Accordion.Body>
                <Form.Check
                  type="checkbox"
                  id="flooringAddToBid"
                  label="Add to bid"
                  checked={flooring.addToBid}
                  onChange={(e)=>setFlooring(prev=>({ ...prev, addToBid: e.target.checked }))}
                  className="mb-3"
                />

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small text-muted w-100">Area (ft²)</Form.Label>
                      <Form.Control 
                        type="number"
                        value={flooring.area}
                        onChange={(e)=>setFlooring({ ...flooring, area: Math.max(0, parseFloat(e.target.value)||0) })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="small text-muted w-100">Coats</Form.Label>
                      <Form.Control 
                        type="number"
                        value={flooring.coats}
                        onChange={(e)=>setFlooring({ ...flooring, coats: Math.max(1, parseInt(e.target.value||'1',10)) })}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="small text-muted w-100">Price per coat</Form.Label>
                  <Form.Control 
                    type="number"
                    step="0.01"
                    value={flooring.price_per_coat}
                    onChange={(e)=>setFlooring({ ...flooring, price_per_coat: Math.max(0, parseFloat(e.target.value)||0) })}
                  />
                </Form.Group>

                <div className="small text-muted text-start">
                  Flooring total: <strong>{fmt(calc.flooringCost, currency)}</strong>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Recurring Janitorial Section */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="janitorial">
              <Accordion.Header 
                className="bg-primary bg-opacity-10"
                style={{
                  '--bs-accordion-active-bg': 'rgba(13, 110, 253, 0.9)',
                  '--bs-accordion-active-color': '#ffffff',
                  '--bs-accordion-btn-focus-box-shadow': 'none'
                }}
              >
                <div className="d-flex align-items-center">
                  <i className={`fas fa-broom me-2 ${activeKeys.includes('janitorial') ? 'text-white' : 'text-primary'}`}></i>
                  <span className={`fw-bold ${activeKeys.includes('janitorial') ? 'text-white' : 'text-primary'}`}>Recurring Janitorial</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-3 bg-primary bg-opacity-10">
                <Row className="mb-3 gap-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Preset</Form.Label>
                      <Form.Select 
                        size="sm"
                        value={janitorial.preset} 
                        onChange={(e)=>{ const preset = e.target.value; setJanitorial(prev=>({ ...prev, preset })); if (preset) applyJanitorialPreset(preset); }}
                      >
                        <option value="">— Custom —</option>
                        <option value="office_5x">Office 5×/week</option>
                        <option value="retail_7x">Retail 7×/week</option>
                        <option value="industrial_3x">Industrial 3×/week</option>
                        <option value="school_5x">School 5×/week</option>
                        <option value="healthcare_7x_deep">Healthcare 7×/week (deep)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Visits per day</Form.Label>
                      <Form.Control 
                        type="number"
                        size="sm"
                        value={janitorial.visits_per_day}
                        onChange={(e)=>setJanitorial({ ...janitorial, visits_per_day: Math.max(0, parseFloat(e.target.value)||0) })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Days per week</Form.Label>
                      <Form.Control 
                        type="number"
                        size="sm"
                        value={janitorial.days_per_week}
                        onChange={(e)=>setJanitorial({ ...janitorial, days_per_week: Math.max(0, parseFloat(e.target.value)||0) })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Scope</Form.Label>
                      <Form.Select 
                        size="sm"
                        value={janitorial.scope}
                        onChange={(e)=>setJanitorial({ ...janitorial, scope: e.target.value })}
                      >
                        <option value="light">Light</option>
                        <option value="standard">Standard</option>
                        <option value="deep">Deep</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Apply basis</Form.Label>
                      <Form.Select 
                        size="sm"
                        value={janitorial.apply_basis}
                        onChange={(e)=>setJanitorial({ ...janitorial, apply_basis: e.target.value })}
                      >
                        <option value="per_visit">Per visit</option>
                        <option value="monthly">Monthly</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="small text-muted fw-bold w-100">Supplies %</Form.Label>
                      <Form.Control 
                        type="number"
                        size="sm"
                        value={janitorial.supplies_pct}
                        onChange={(e)=>setJanitorial({ ...janitorial, supplies_pct: Math.max(0, parseFloat(e.target.value)||0) })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12} className="d-flex align-items-end ms-2">
                    <Form.Check
                      type="checkbox"
                      id="janitorialApplyToSelected"
                      label="Use as Selected Bid"
                      checked={janitorial.apply_to_selected}
                      onChange={(e)=>setJanitorial(prev=>({ ...prev, apply_to_selected: e.target.checked }))}
                      className="small mb-3"
                    />
                  </Col>
                </Row>

                <div className="small mb-0 text-muted">
                  <div className="d-flex flex-wrap gap-3">
                    <span>Per-visit: <strong>{fmt(calc.janTotalsPerVisit[selectedModel], currency)}</strong></span>
                    <span>•</span>
                    <span>Weekly: <strong>{fmt(calc.janTotalsWeekly[selectedModel], currency)}</strong></span>
                    <span>•</span>
                    <span>Monthly: <strong>{fmt(calc.janTotalsMonthly[selectedModel], currency)}</strong></span>
                    <span>•</span>
                    <span>Annual: <strong>{fmt(calc.janTotalsAnnual[selectedModel], currency)}</strong></span>
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Proposal Export Data Section */}
          <Accordion activeKey={activeKeys} onSelect={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])} alwaysOpen className="mb-4">
            <Accordion.Item eventKey="proposalExport">
              <Accordion.Header 
                className="bg-success bg-opacity-10"
                style={{
                  '--bs-accordion-active-bg': 'rgba(25, 135, 84, 0.9)',
                  '--bs-accordion-active-color': '#ffffff',
                  '--bs-accordion-btn-focus-box-shadow': 'none'
                }}
              >
                <div className="d-flex align-items-center">
                  <i className={`fas fa-file-export me-2 ${activeKeys.includes('proposalExport') ? 'text-white' : 'text-success'}`}></i>
                  <span className={`fw-bold ${activeKeys.includes('proposalExport') ? 'text-white' : 'text-success'}`}>Proposal Export Data</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="p-4 bg-white text-start">
                <div className="font-monospace small">
                  {/* Selected Bid Amount */}
                  <div className="mb-4">
                    <div className="fw-bold text-dark mb-2">SELECTED BID AMOUNT</div>
                    <div className="ms-3">
                      {calc.itemizedModelPost.pressure > 0 && (
                        <div>Pressure Washing: <strong>{fmt(calc.itemizedModelPost.pressure, currency)}</strong></div>
                      )}
                      {calc.itemizedModelPost.windows > 0 && (
                        <div>Exterior Windows: <strong>{fmt(calc.itemizedModelPost.windows, currency)}</strong></div>
                      )}
                      <div>Total: <strong>{fmt(calc.selectedBid, currency)}</strong></div>
                      {/* {janitorial.apply_to_selected && (
                        <div>Basis: {janitorial.apply_basis === 'per_visit' ? 'Per Visit' : 'Monthly'}</div>
                      )} */}
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="mb-4">
                    <div className="fw-bold text-dark mb-2">PROJECT DETAILS</div>
                    <div className="ms-3">
                      <div>Type: <strong>{projectType}</strong></div>
                      <div>Location: <strong>{stateProvince}, {country}</strong></div>
                      <div>Area: <strong>{fmt0(building.area)} {unitAreaLabel}</strong></div>
                      {/* <div>Levels: <strong>{building.levels}</strong></div>
                      {building.useDetailed && building.details.filter(d => d.area > 0).length > 0 && (
                        <div>Buildings: <strong>{building.details.filter(d => d.area > 0).map(d => `${d.name} (${fmt0(d.area)} ${unitAreaLabel})`).join(', ')}</strong></div>
                      )} */}
                    </div>
                  </div>
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Selected Bid Section */}
          <Card className="mb-4 text-start">
            <Card.Body className="py-3 px-2">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold mb-0">
                  {manHour.apply_to_selected ? 'Selected Bid - Time & Material' : 
                   janitorial.apply_to_selected ? 'Selected Bid - Janitorial Service' : 'Selected Bid'}
                </h4>
                {/* Quick-switch buttons - only show when not using T&M */}
                <ButtonGroup size="md" className="gap-2">
                  <Button
                    className="rounded-3"
                    variant={selectedModel === 'market_win' ? 'primary' : 'outline-secondary'}
                    onClick={() => handleSelectModelChange('market_win')}
                  >
                    MW
                  </Button>
                  <Button 
                    className="rounded-3"
                    variant={selectedModel === 'lean' ? 'primary' : 'outline-secondary'}
                    onClick={() => handleSelectModelChange('lean')}
                  >
                    Lean
                  </Button>
                  <Button 
                    className="rounded-3"
                    variant={selectedModel === 'profit_heavy' ? 'primary' : 'outline-secondary'}
                    onClick={() => handleSelectModelChange('profit_heavy')}
                  >
                    PH
                  </Button>
                </ButtonGroup>
              </div>

              <div className="display-4 fw-bold text-black mb-2">
                {fmt(calc.selectedBid, currency)}
              </div>

              {manHour.apply_to_selected && (
                <div className="small text-primary mb-3">
                  Showing Time & Material pricing • Direct hourly billing + add-ons
                </div>
              )}

              {janitorial.apply_to_selected && (
                <div className="small text-primary mb-3">
                  Showing {janitorial.apply_basis === 'per_visit' ? 'per-visit' : 'monthly'} janitorial pricing • Construction cleaning not included
                </div>
              )}

              {/* Pricing Methods (non-clickable cards) - only show when not using T&M or Janitorial */}
              {/* {!manHour.apply_to_selected && !janitorial.apply_to_selected && ( */}
                <div className="mt-4">
                  <h4 className="fw-bold text-muted mb-3">
                    {janitorial.apply_to_selected ? 'Janitorial Pricing Methods' : 'Pricing Methods'}
                  </h4>
                  <Row className="g-2">
                    <Col md={12}>
                      <Card className={`h-100 px-3 rounded-3 ${selectedModel === 'market_win' ? 'border-primary border-2' : ''}`}>
                        <Card.Body className="p-3">
                          <Card.Title className="h3 mb-1">Market-Win</Card.Title>
                          <Card.Text className="small text-muted mb-2">
                            minimal margin to beat competitive bids.
                          </Card.Text>
                          <div className="h3 fw-bold text-dark">
                            {janitorial.apply_to_selected 
                              ? fmt(janitorial.apply_basis === 'per_visit' ? calc.janTotalsPerVisit.market_win : calc.janTotalsMonthly.market_win, currency)
                              : fmt(calc.totals.market_win, currency)
                            }
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={12}>
                      <Card className={`h-100 px-3 rounded-3 ${selectedModel === 'lean' ? 'border-primary border-2' : ''}`}>
                        <Card.Body className="p-3">
                          <Card.Title className="h3 mb-1">Lean</Card.Title>
                          <Card.Text className="small text-muted mb-2">
                            balanced margin for healthy win rate.
                          </Card.Text>
                          <div className="h3 fw-bold text-dark">
                            {janitorial.apply_to_selected 
                              ? fmt(janitorial.apply_basis === 'per_visit' ? calc.janTotalsPerVisit.lean : calc.janTotalsMonthly.lean, currency)
                              : fmt(calc.totals.lean, currency)
                            }
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={12}>
                      <Card className={`h-100 px-3 rounded-3 ${selectedModel === 'profit_heavy' ? 'border-primary border-2' : ''}`}>
                        <Card.Body className="p-3">
                          <Card.Title className="h3 mb-1">Profit-Heavy</Card.Title>
                          <Card.Text className="small text-muted mb-2">
                            premium margin for complex/rush/high-risk work.
                          </Card.Text>
                          <div className="h3 fw-bold text-dark">
                            {janitorial.apply_to_selected 
                              ? fmt(janitorial.apply_basis === 'per_visit' ? calc.janTotalsPerVisit.profit_heavy : calc.janTotalsMonthly.profit_heavy, currency)
                              : fmt(calc.totals.profit_heavy, currency)
                            }
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              {/* )} */}

              {/* Itemization blocks (hide $0 rows) */}
              {calc.selectedBreakdownMode === 'model' && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="fw-bold mb-0">Cost Breakdown</h4>
                    <Button 
                      variant="outline-secondary" 
                      size="md"
                      className="rounded-3"
                      onClick={() => setShowItemizationInfo((v) => !v)}
                    >
                      {showItemizationInfo ? 'Hide' : 'Show'} Info
                    </Button>
                  </div>
                  
                  {showItemizationInfo && (
                    <div className="small mb-3 bg-light p-3 rounded-3 text-start">
                      <Row>
                        <Col md={12}>
                          <div className="fw-bold mb-2">Core Components:</div>
                          <div className="small">
                            <div>Core phases: Main cleaning work (labor hours × hourly rate × phase complexity multipliers)</div>
                            <div>Effective ft² seed: Base rate per square foot adjusted for location, project type, and phase count</div>
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="fw-bold mt-2 mb-1">Add-on Services:</div>
                          <div className="small">
                            <div>Windows: Window cleaning including lift equipment if needed</div>
                            <div>Pressure: Exterior pressure washing (sidewalks, building perimeter)</div>
                            <div>Travel: Site visit costs beyond the first 2 included visits</div>
                            <div>Units: Per-unit pricing for residential/apartment cleaning</div>
                          </div>
                        </Col>
                      </Row>
                      <hr />
                      <div className="small text-muted">
                        All costs include the selected margin ({selectedModel === 'market_win' ? '7%' : selectedModel === 'lean' ? '15%' : '28%'}) and are only included if the corresponding service is enabled and marked "Add to bid".
                        {calc.wagePercent > 0 && (
                          <div className="mb-0 mt-1 mx-0">
                            <i className="fas fa-info-circle me-1"></i>
                            <strong>Special Labor Requirements Applied:</strong> All costs include a {calc.wagePercent.toFixed(0)}% increase
                            {davis.checked && union.checked ? ' (Davis-Bacon + Union)' : davis.checked ? ' (Davis-Bacon)' : ' (Union)'}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <Row className="g-2">
                    {nz(calc.itemizedModelPost.core_phases) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Core phases</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.core_phases, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedModelPost.effective_ft2_seed) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Effective ft² seed</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.effective_ft2_seed, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedModelPost.travel) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Travel</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.travel, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedModelPost.windows) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Windows</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.windows, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedModelPost.pressure) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Pressure</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.pressure, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedModelPost.flooring) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Flooring</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.flooring, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedModelPost.units_simple) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Units (Simple)</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.units_simple, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedModelPost.units_mix) && (
                      <Col md={12}>
                        <Card className="border px-3">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Units (Mix)</div>
                            <div className="fw-bold">{fmt(calc.itemizedModelPost.units_mix, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    <Col md={12}>
                      <Card className="bg-light border px-3">
                        <Card.Body className="p-2">
                          <div className="small">Sum</div>
                          <div className="fw-bold">{fmt(calc.itemizedModelPostSum, currency)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}

              {/* T&M Itemization */}
              {calc.selectedBreakdownMode === 'tm' && (
                <div className="mt-4">
                  <h6 className="fw-bold mb-3">T&M Breakdown</h6>
                  <Row className="g-2">
                    {nz(calc.itemizedTm.labor_tm) && (
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Labor (T&M)</div>
                            <div className="fw-bold">{fmt(calc.itemizedTm.labor_tm, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedTm.travel) && (
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Travel</div>
                            <div className="fw-bold">{fmt(calc.itemizedTm.travel, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedTm.windows) && (
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Windows</div>
                            <div className="fw-bold">{fmt(calc.itemizedTm.windows, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedTm.pressure) && (
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Pressure</div>
                            <div className="fw-bold">{fmt(calc.itemizedTm.pressure, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedTm.flooring) && (
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Flooring</div>
                            <div className="fw-bold">{fmt(calc.itemizedTm.flooring, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedTm.units_simple) && (
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Units (Simple)</div>
                            <div className="fw-bold">{fmt(calc.itemizedTm.units_simple, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    {nz(calc.itemizedTm.units_mix) && (
                      <Col md={4}>
                        <Card className="bg-light border-0">
                          <Card.Body className="p-2">
                            <div className="small text-muted">Units (Mix)</div>
                            <div className="fw-bold">{fmt(calc.itemizedTm.units_mix, currency)}</div>
                          </Card.Body>
                        </Card>
                      </Col>
                    )}
                    <Col md={4}>
                      <Card className="bg-secondary text-white">
                        <Card.Body className="p-2">
                          <div className="small">Sum</div>
                          <div className="fw-bold">{fmt(calc.itemizedTmSum, currency)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Janitorial Breakdown */}
              {calc.selectedBreakdownMode === 'janitorial' && (
                <div className="mt-4">
                  <h4 className="fw-bold mb-3">Janitorial Breakdown</h4>
                  <Row className="g-2">
                    <Col md={12}>
                      <Card className="border px-3 py-1">
                        <Card.Body className="p-2">
                          <div className="small text-muted">Per visit</div>
                          <div className="fw-bold">{fmt(calc.janTotalsPerVisit[selectedModel], currency)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={12}>
                      <Card className="border px-3 py-1">
                        <Card.Body className="p-2">
                          <div className="small text-muted">Weekly</div>
                          <div className="fw-bold">{fmt(calc.janTotalsWeekly[selectedModel], currency)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={12}>
                      <Card className="border px-3 py-1">
                        <Card.Body className="p-2">
                          <div className="small text-muted">Monthly</div>
                          <div className="fw-bold">{fmt(calc.janTotalsMonthly[selectedModel], currency)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={12}>
                      <Card className="border px-3 py-1">
                        <Card.Body className="p-2">
                          <div className="small text-muted">Annual</div>
                          <div className="fw-bold">{fmt(calc.janTotalsAnnual[selectedModel], currency)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={12}>
                      <Card className="border px-3 py-1">
                        <Card.Body className="p-2">
                          <div className="small text-muted">Supplies (monthly)</div>
                          <div className="fw-bold">{fmt(calc.janSuppliesMonthly, currency)}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}

              {/* Phase breakdown at the very bottom of Selected Bid */}
              {showPhaseBreakout && (
                <div className="mt-4 pt-3 border-top">
                  <div className="small fw-bold text-muted mb-2">Phase breakdown</div>
                  <div className="small text-muted">
                    Rough hours: <strong>{fmt0(calc.roughHrs)}</strong> • Final: <strong>{fmt0(calc.finalHrs)}</strong> • Fluff: <strong>{fmt0(calc.fluffHrs)}</strong>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
});

export default CleaningCalculator;
